var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import * as THREE from "three";
import { Vector3, Vector2, Color, TextureLoader, SRGBColorSpace, ClampToEdgeWrapping, LinearMipmapLinearFilter, LinearFilter, DataTexture, RGBAFormat, Quaternion, Box3, BoxHelper, AnimationMixer, MathUtils, WebGLRenderer, Scene, PerspectiveCamera, DirectionalLight, HemisphereLight, Mesh, CircleGeometry, MeshBasicMaterial, LoadingManager, ShaderMaterial, LoopRepeat } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { clampParameterValue, MANDATORY_PACK_FILENAMES, KNOWN_OPTIONAL_EYE_EXPRESSION_IDS, KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS } from "@nxorbit/shared-schema";
import { KNOWN_OPTIONAL_EYE_EXPRESSION_IDS as KNOWN_OPTIONAL_EYE_EXPRESSION_IDS2, KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS as KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS2, MALFEUERA_SAMPLE_MODEL_URL, MALFEUERA_SAMPLE_PACK, MANDATORY_PACK_FILENAMES as MANDATORY_PACK_FILENAMES2, Model3DActionPlanSchema, Model3DActionSchema, Model3DTexturePackSchema, OL_SAMPLE_MODEL_URL, OL_SAMPLE_PACK, PARAMETER_IDS, clampParameterValue as clampParameterValue2 } from "@nxorbit/shared-schema";
const AXIS_TRANSFORM$1 = {
  x: "transformed.x += breathAmount;",
  y: "transformed.y += breathAmount;",
  z: "transformed.z += breathAmount;"
};
function createBreathUniforms(action, modelBounds, sharedTimeRef) {
  return {
    uTime: sharedTimeRef,
    uBreathEnabled: { value: action.enabled ? 1 : 0 },
    uBreathStrength: { value: action.params.strength },
    uBreathSpeed: { value: action.params.speed },
    uMaskStart: { value: action.params.maskStart },
    uMaskSoftness: { value: action.params.maskSoftness },
    uModelMinY: { value: modelBounds.minY },
    uModelHeight: { value: Math.max(modelBounds.height, 1e-4) },
    axis: action.params.axis
  };
}
function applyBreathShader(material, uniforms) {
  const axisLine = AXIS_TRANSFORM$1[uniforms.axis];
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, {
      uTime: uniforms.uTime,
      uBreathEnabled: uniforms.uBreathEnabled,
      uBreathStrength: uniforms.uBreathStrength,
      uBreathSpeed: uniforms.uBreathSpeed,
      uMaskStart: uniforms.uMaskStart,
      uMaskSoftness: uniforms.uMaskSoftness,
      uModelMinY: uniforms.uModelMinY,
      uModelHeight: uniforms.uModelHeight
    });
    shader.vertexShader = `
      uniform float uTime;
      uniform float uBreathEnabled;
      uniform float uBreathStrength;
      uniform float uBreathSpeed;
      uniform float uMaskStart;
      uniform float uMaskSoftness;
      uniform float uModelMinY;
      uniform float uModelHeight;
    ${shader.vertexShader}`;
    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `
      #include <begin_vertex>
      float breathWorldY = (modelMatrix * vec4(position, 1.0)).y;
      float height01 = clamp((breathWorldY - uModelMinY) / max(uModelHeight, 0.0001), 0.0, 1.0);
      float mask = smoothstep(uMaskStart, min(uMaskStart + uMaskSoftness, 1.0), height01);
      // Heartbeat layer — sharpened sine thump at ~1.18 Hz (71 bpm),
      // 12% of breath amplitude, deliberately NOT a multiple of the
      // breath speed so the two never sync. Mirrors the composed
      // shader (composed-character.glsl.ts) breath block.
      float heartThump = pow(max(sin(uTime * 7.4), 0.0), 6.0) * 0.12;
      float breathAmount = (sin(uTime * uBreathSpeed) + heartThump) * uBreathStrength * uBreathEnabled * mask;
      ${axisLine}
      `
    );
  };
  material.customProgramCacheKey = () => `proc:breath-v3-world-height-heart:${uniforms.axis}`;
  material.needsUpdate = true;
  material.userData = material.userData ?? {};
  material.userData.proceduralBreath = uniforms;
}
const NOON = {
  key: 16777215,
  keyIntensity: 3.2,
  sky: 16056296,
  ground: 3034439,
  hemiIntensity: 1.65,
  rim: 12447999,
  rimIntensity: 1.1
};
const ANCHORS = [
  // Deep night — cool moonlight, slightly dim, stronger cool rim.
  { hour: 0, key: 14082815, keyIntensity: 2.85, sky: 13424366, ground: 1910581, hemiIntensity: 1.5, rim: 10467560, rimIntensity: 1.3 },
  { hour: 5, key: 14082815, keyIntensity: 2.85, sky: 13424366, ground: 1910581, hemiIntensity: 1.5, rim: 10467560, rimIntensity: 1.3 },
  // Dawn — warm peach key, lavender rim.
  { hour: 7, key: 16769992, keyIntensity: 3, sky: 16308672, ground: 2765378, hemiIntensity: 1.55, rim: 14206184, rimIntensity: 1.1 },
  // Day plateau (noon look held across late morning → afternoon).
  { hour: 10.5, ...NOON },
  { hour: 16, ...NOON },
  // Golden hour — warm key + amber rim.
  { hour: 18.5, key: 16765856, keyIntensity: 3.15, sky: 16175788, ground: 3354670, hemiIntensity: 1.6, rim: 16759178, rimIntensity: 1.25 },
  // Nightfall — meets the 24h wrap back to deep night.
  { hour: 21, key: 14082815, keyIntensity: 2.85, sky: 13424366, ground: 1910581, hemiIntensity: 1.5, rim: 10467560, rimIntensity: 1.3 }
];
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function lerpColor(a, b, t) {
  const ar = a >> 16 & 255;
  const ag = a >> 8 & 255;
  const ab = a & 255;
  const br = b >> 16 & 255;
  const bg = b >> 8 & 255;
  const bb = b & 255;
  return Math.round(lerp(ar, br, t)) << 16 | Math.round(lerp(ag, bg, t)) << 8 | Math.round(lerp(ab, bb, t));
}
function lightBreathFactor(tSeconds) {
  return 1 + 0.04 * Math.sin(tSeconds * 0.7) * Math.sin(tSeconds * 0.23);
}
function hourToLighting(hour) {
  const h = (hour % 24 + 24) % 24;
  let lo = ANCHORS[ANCHORS.length - 1];
  let hi = ANCHORS[0];
  let loHour = lo.hour;
  let hiHour = hi.hour + 24;
  for (let i = 0; i < ANCHORS.length; i++) {
    const a = ANCHORS[i];
    const b = ANCHORS[(i + 1) % ANCHORS.length];
    const bHour = i + 1 === ANCHORS.length ? b.hour + 24 : b.hour;
    if (h >= a.hour && h < bHour) {
      lo = a;
      hi = b;
      loHour = a.hour;
      hiHour = bHour;
      break;
    }
  }
  const span = hiHour - loHour;
  const t = span <= 0 ? 0 : (h - loHour) / span;
  return {
    key: lerpColor(lo.key, hi.key, t),
    keyIntensity: lerp(lo.keyIntensity, hi.keyIntensity, t),
    sky: lerpColor(lo.sky, hi.sky, t),
    ground: lerpColor(lo.ground, hi.ground, t),
    hemiIntensity: lerp(lo.hemiIntensity, hi.hemiIntensity, t),
    rim: lerpColor(lo.rim, hi.rim, t),
    rimIntensity: lerp(lo.rimIntensity, hi.rimIntensity, t)
  };
}
const GAZE_WANDER_IDLE_MS = 5e3;
const GAZE_WANDER_MAX_X = 0.55;
const GAZE_WANDER_MAX_Y = 0.35;
const GAZE_WANDER_CENTER_BIAS = 0.4;
function computeWanderTarget(rand) {
  if (rand() < GAZE_WANDER_CENTER_BIAS) return { x: 0, y: 0 };
  return {
    x: (rand() * 2 - 1) * GAZE_WANDER_MAX_X,
    y: (rand() * 2 - 1) * GAZE_WANDER_MAX_Y
  };
}
function nextWanderDelayMs(rand) {
  return 2500 + rand() * 3e3;
}
const C = 4.5;
const SETTLE_EPS = 0.01;
function pokeImpulse(s, strength = 1) {
  return { amp: s.amp, vel: s.vel + 6 * strength };
}
function stepPokeSpring(s, dt) {
  const h = Math.min(dt, 0.05);
  const acc = -90 * s.amp - C * s.vel;
  const vel = s.vel + acc * h;
  const amp = s.amp + vel * h;
  return { amp, vel };
}
function isPokeSettled(s) {
  return Math.abs(s.amp) < SETTLE_EPS && Math.abs(s.vel) < SETTLE_EPS * 20;
}
const POKE_COMBO_COUNT = 5;
const POKE_COMBO_WINDOW_MS = 3e3;
const POKE_COMBO_COOLDOWN_MS = 1e4;
function createPokeComboState() {
  return { times: [], lastTriggeredAt: -Infinity };
}
function registerPoke(state, now) {
  if (now - state.lastTriggeredAt < POKE_COMBO_COOLDOWN_MS) {
    return { state, triggered: false };
  }
  const times = [...state.times, now].filter((t) => now - t <= POKE_COMBO_WINDOW_MS);
  if (times.length >= POKE_COMBO_COUNT) {
    return { state: { times: [], lastTriggeredAt: now }, triggered: true };
  }
  return { state: { times, lastTriggeredAt: state.lastTriggeredAt }, triggered: false };
}
const AXIS_TRANSFORM = {
  x: "transformed.x += swayAmount;",
  z: "transformed.z += swayAmount;"
};
function createSwayUniforms(action, modelBounds, sharedTimeRef) {
  return {
    uTime: sharedTimeRef,
    uSwayEnabled: { value: action.enabled ? 1 : 0 },
    uSwayAmplitude: { value: action.params.amplitude },
    uSwaySpeed: { value: action.params.speed },
    uMaskStart: { value: action.params.maskStart },
    uMaskSoftness: { value: action.params.maskSoftness },
    uModelMinY: { value: modelBounds.minY },
    uModelHeight: { value: Math.max(modelBounds.height, 1e-4) },
    axis: action.params.axis
  };
}
function applySwayShader(material, uniforms) {
  const axisLine = AXIS_TRANSFORM[uniforms.axis];
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, {
      uTime: uniforms.uTime,
      uSwayEnabled: uniforms.uSwayEnabled,
      uSwayAmplitude: uniforms.uSwayAmplitude,
      uSwaySpeed: uniforms.uSwaySpeed,
      uMaskStart: uniforms.uMaskStart,
      uMaskSoftness: uniforms.uMaskSoftness,
      uModelMinY: uniforms.uModelMinY,
      uModelHeight: uniforms.uModelHeight
    });
    shader.vertexShader = `
      uniform float uTime;
      uniform float uSwayEnabled;
      uniform float uSwayAmplitude;
      uniform float uSwaySpeed;
      uniform float uMaskStart;
      uniform float uMaskSoftness;
      uniform float uModelMinY;
      uniform float uModelHeight;
    ${shader.vertexShader}`;
    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `
      #include <begin_vertex>
      float swayWorldY = (modelMatrix * vec4(position, 1.0)).y;
      float height01 = clamp((swayWorldY - uModelMinY) / max(uModelHeight, 0.0001), 0.0, 1.0);
      float mask = smoothstep(uMaskStart, min(uMaskStart + uMaskSoftness, 1.0), height01);
      // Wind gust envelope — mirrors composed-character.glsl: two slow
      // incommensurate sines modulate amplitude 0.5x–1.0x so the sway
      // gusts and lulls like real wind, not a metronome.
      float gust = 0.75 + 0.25 * sin(uTime * 0.37 + 1.7) * sin(uTime * 0.13);
      float swayAmount = sin(uTime * uSwaySpeed) * gust * uSwayAmplitude * uSwayEnabled * mask * height01;
      ${axisLine}
      `
    );
  };
  material.customProgramCacheKey = () => `proc:sway-v3-world-height-gust:${uniforms.axis}`;
  material.needsUpdate = true;
  material.userData = material.userData ?? {};
  material.userData.proceduralSway = uniforms;
}
const composedCharacterVertexShader = (
  /* glsl */
  `
  #include <common>
  #include <skinning_pars_vertex>

  // Procedural shader actions — see openspec/changes/procedural-shader-actions.
  // All zero-init: when no breath action is configured, uBreathEnabled=0 and
  // the displacement collapses to zero, so this is a no-op for characters
  // without procedural motion.
  uniform float uProcTime;
  uniform float uBreathEnabled;
  uniform float uBreathStrength;
  uniform float uBreathSpeed;
  uniform float uBreathMaskStart;
  uniform float uBreathMaskSoftness;
  uniform float uBreathModelMinY;
  uniform float uBreathModelHeight;
  uniform vec3 uBreathAxis;
  // Sway — horizontal pendulum; amplitude grows with height. Shares
  // uProcTime. Zero-init → no-op when no sway action is configured.
  uniform float uSwayEnabled;
  uniform float uSwayAmplitude;
  uniform float uSwaySpeed;
  uniform float uSwayMaskStart;
  uniform float uSwayMaskSoftness;
  uniform float uSwayModelMinY;
  uniform float uSwayModelHeight;
  uniform vec3 uSwayAxis;

  varying vec2 vUv;

  void main() {
    vUv = uv;
    vec3 transformed = position;

    // Breath — apply BEFORE skinning so the mascot's whole rest pose
    // moves together; skinned characters with bones would override
    // this per-vertex if they have animation, which is intentional.
    {
      float worldY = (modelMatrix * vec4(position, 1.0)).y;
      float height01 = clamp((worldY - uBreathModelMinY) / max(uBreathModelHeight, 0.0001), 0.0, 1.0);
      float mask = smoothstep(uBreathMaskStart, min(uBreathMaskStart + uBreathMaskSoftness, 1.0), height01);
      // Second-order heartbeat layered on the breath: a sharpened sine
      // (pow^6 -> brief thump, long rest) at ~1.18 Hz (71 bpm), fixed
      // rate so it never phase-locks with the operator's breath speed.
      // 12% of breath amplitude, same chest mask -> reads as a pulse
      // under the breathing, not a second breath.
      float heartThump = pow(max(sin(uProcTime * 7.4), 0.0), 6.0) * 0.12;
      float amt = (sin(uProcTime * uBreathSpeed) + heartThump) * uBreathStrength * uBreathEnabled * mask;
      transformed += uBreathAxis * amt;
    }

    // Sway — horizontal pendulum lean; offset grows with normalized
    // height (planted base, swaying top). Same shared uProcTime clock.
    {
      float swayWorldY = (modelMatrix * vec4(position, 1.0)).y;
      float sHeight01 = clamp((swayWorldY - uSwayModelMinY) / max(uSwayModelHeight, 0.0001), 0.0, 1.0);
      float sMask = smoothstep(uSwayMaskStart, min(uSwayMaskStart + uSwayMaskSoftness, 1.0), sHeight01);
      // Wind gust envelope: two slow incommensurate sines (0.37 +
      // 0.13 rad/s — never repeat together) modulate the amplitude
      // between 0.5x and 1.0x, so the sway breathes like wind gusting
      // and lulling instead of a constant-amplitude metronome.
      float gust = 0.75 + 0.25 * sin(uProcTime * 0.37 + 1.7) * sin(uProcTime * 0.13);
      float sAmt = sin(uProcTime * uSwaySpeed) * gust * uSwayAmplitude * uSwayEnabled * sMask * sHeight01;
      transformed += uSwayAxis * sAmt;
    }

    #include <skinbase_vertex>
    #include <skinning_vertex>

    gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
  }
`
);
const composedCharacterFragmentShader = (
  /* glsl */
  `
  uniform sampler2D uMainTex;
  uniform sampler2D uEyeTex;
  uniform sampler2D uEyeTexNext;
  uniform sampler2D uCloseEyeTex;
  uniform sampler2D uEyeMask;
  uniform sampler2D uMouthTex;
  uniform sampler2D uMouthTexNext;
  uniform float uMouthMix;
  uniform sampler2D uMouthMask;
  uniform vec3 uTint;
  uniform vec3 uEyeTint;
  uniform vec3 uMouthTint;
  uniform vec2 uEyeOffset;
  uniform vec2 uEyeScale;
  uniform vec2 uMouthOffset;
  uniform vec2 uMouthScale;
  uniform float uEyeBlend;
  uniform float uMouthBlend;
  uniform float uMouthOpacity;
  uniform float uExpressionMix;
  uniform float uExpressionOpacityFrom;
  uniform float uExpressionOpacityTo;
  uniform float uEyeMaskInvert;
  uniform float uMouthMaskInvert;

  varying vec2 vUv;

  vec2 regionUv(vec2 uv, vec2 scale, vec2 offset) {
    vec2 safeScale = max(abs(scale), vec2(0.0001));
    return (uv - 0.5) / safeScale + 0.5 + offset;
  }

  void main() {
    vec4 baseCol = texture2D(uMainTex, vUv) * vec4(uTint, 1.0);

    float eyeMask = texture2D(uEyeMask, vUv).r;
    eyeMask = mix(eyeMask, 1.0 - eyeMask, step(0.5, uEyeMaskInvert));
    vec4 eyeColA = texture2D(uEyeTex, regionUv(vUv, uEyeScale, uEyeOffset)) * vec4(uEyeTint, 1.0);
    vec4 eyeColB = texture2D(uEyeTexNext, regionUv(vUv, uEyeScale, uEyeOffset)) * vec4(uEyeTint, 1.0);
    vec4 eyeCol = mix(eyeColA, eyeColB, uExpressionMix);
    float expressionOpacity = mix(uExpressionOpacityFrom, uExpressionOpacityTo, uExpressionMix);
    vec4 closeEyeCol = texture2D(uCloseEyeTex, regionUv(vUv, uEyeScale, uEyeOffset)) * vec4(uEyeTint, 1.0);

    float mouthMask = texture2D(uMouthMask, vUv).r;
    mouthMask = mix(mouthMask, 1.0 - mouthMask, step(0.5, uMouthMaskInvert));
    vec4 mouthColCur  = texture2D(uMouthTex,     regionUv(vUv, uMouthScale, uMouthOffset)) * vec4(uMouthTint, 1.0);
    vec4 mouthColNext = texture2D(uMouthTexNext, regionUv(vUv, uMouthScale, uMouthOffset)) * vec4(uMouthTint, 1.0);
    vec4 mouthCol = mix(mouthColCur, mouthColNext, uMouthMix);

    float aEye = clamp(eyeMask * eyeCol.a * expressionOpacity, 0.0, 1.0);
    float aCloseEye = clamp(eyeMask * closeEyeCol.a * uEyeBlend, 0.0, 1.0);
    float aMouth = clamp(mouthMask * mouthCol.a * uMouthBlend * uMouthOpacity, 0.0, 1.0);

    vec3 color = baseCol.rgb;
    color = mix(color, eyeCol.rgb, aEye);
    color = mix(color, closeEyeCol.rgb, aCloseEye);
    color = mix(color, mouthCol.rgb, aMouth);
    // Force alpha to 1.0 — model is solid 3D; page transparency
    // comes from renderer clear color, not per-fragment alpha.
    // (Mobile mipmap bleed from Base.png soft edges → silhouette
    // halo on small viewports. Desktop hides it via high-mip sampling.)
    gl_FragColor = vec4(color, 1.0);
    #include <colorspace_fragment>
  }
`
);
function createComposedUniforms() {
  return {
    uMainTex: { value: null },
    uEyeTex: { value: null },
    uEyeTexNext: { value: null },
    uCloseEyeTex: { value: null },
    uEyeMask: { value: null },
    uMouthTex: { value: null },
    uMouthTexNext: { value: null },
    uMouthMix: { value: 0 },
    uMouthMask: { value: null },
    uTint: { value: new Color(1, 1, 1) },
    uEyeTint: { value: new Color(1, 1, 1) },
    uMouthTint: { value: new Color(1, 1, 1) },
    uEyeOffset: { value: new Vector2(0, 0) },
    uEyeScale: { value: new Vector2(1, 1) },
    uMouthOffset: { value: new Vector2(0, 0) },
    uMouthScale: { value: new Vector2(1, 1) },
    uEyeBlend: { value: 0 },
    uMouthBlend: { value: 0 },
    uMouthOpacity: { value: 1 },
    uExpressionMix: { value: 0 },
    uExpressionOpacityFrom: { value: 0 },
    uExpressionOpacityTo: { value: 0 },
    uEyeMaskInvert: { value: 1 },
    uMouthMaskInvert: { value: 1 },
    uProcTime: { value: 0 },
    uBreathEnabled: { value: 0 },
    uBreathStrength: { value: 0 },
    uBreathSpeed: { value: 0 },
    uBreathMaskStart: { value: 0 },
    uBreathMaskSoftness: { value: 0 },
    uBreathModelMinY: { value: 0 },
    uBreathModelHeight: { value: 1 },
    uBreathAxis: { value: new Vector3(0, 1, 0) },
    uSwayEnabled: { value: 0 },
    uSwayAmplitude: { value: 0 },
    uSwaySpeed: { value: 0 },
    uSwayMaskStart: { value: 0 },
    uSwayMaskSoftness: { value: 0 },
    uSwayModelMinY: { value: 0 },
    uSwayModelHeight: { value: 1 },
    uSwayAxis: { value: new Vector3(1, 0, 0) }
  };
}
function makeTransparentFallback() {
  const data = new Uint8Array([0, 0, 0, 0]);
  const tex = new DataTexture(data, 1, 1, RGBAFormat);
  tex.needsUpdate = true;
  return configure(tex);
}
function configure(tex) {
  tex.colorSpace = SRGBColorSpace;
  tex.flipY = false;
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  return tex;
}
function loadOne(loader, url) {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (tex) => resolve(configure(tex)),
      void 0,
      (err) => reject(err instanceof Error ? err : new Error(`failed to load ${url}: ${String(err)}`))
    );
  });
}
async function loadTexturePack(pack) {
  const loader = new TextureLoader();
  let sharedFallback = null;
  const fallback = () => {
    if (!sharedFallback) sharedFallback = makeTransparentFallback();
    return sharedFallback;
  };
  const loadOrFallback = async (url) => {
    if (!url) return fallback();
    try {
      return await loadOne(loader, url);
    } catch (err) {
      console.warn("[texture-loader] slot load failed, using transparent fallback", url, err);
      return fallback();
    }
  };
  const v = pack.visemes ?? {};
  const [base, eyeMask, eyeNormal, eyeBlink, mouthMask, idle, A, E, I, O, U] = await Promise.all([
    loadOrFallback(pack.base),
    loadOrFallback(pack.eyeMask),
    loadOrFallback(pack.eyeNormal),
    loadOrFallback(pack.eyeBlink),
    loadOrFallback(pack.mouthMask),
    loadOrFallback(v.idle),
    loadOrFallback(v.A),
    loadOrFallback(v.E),
    loadOrFallback(v.I),
    loadOrFallback(v.O),
    loadOrFallback(v.U)
  ]);
  const expressions = /* @__PURE__ */ new Map();
  for (const entry of pack.expressions) {
    if (entry.url === null) continue;
    try {
      expressions.set(entry.id, await loadOne(loader, entry.url));
    } catch (err) {
      console.warn("[texture-loader] expression load failed, skipping", entry.id, entry.url, err);
    }
  }
  const ownedTextures = /* @__PURE__ */ new Set([base, eyeMask, eyeNormal, eyeBlink, mouthMask, idle, A, E, I, O, U, ...expressions.values()]);
  return {
    base,
    eyeMask,
    eyeNormal,
    eyeBlink,
    mouthMask,
    visemes: { idle, A, E, I, O, U },
    expressions,
    dispose() {
      for (const t of ownedTextures) t.dispose();
    }
  };
}
const VISEMES = ["idle", "A", "E", "I", "O", "U"];
const FLOORS = [
  { name: "idle", floor: 0 },
  { name: "A", floor: 0.04 },
  { name: "E", floor: 0.09 },
  { name: "I", floor: 0.16 },
  { name: "O", floor: 0.24 },
  { name: "U", floor: 0.34 }
];
const IDLE_THRESHOLD = 0.025;
function pickViseme(smoothedRms, elapsedSeconds, driftRate = 9) {
  if (smoothedRms < IDLE_THRESHOLD) return "idle";
  let baseIndex = 1;
  for (let i = 1; i < FLOORS.length; i++) {
    if (smoothedRms >= FLOORS[i].floor) baseIndex = i;
  }
  const drift = Math.floor(elapsedSeconds * driftRate + smoothedRms * 11) % 3;
  const idx = Math.max(1, Math.min(VISEMES.length - 1, baseIndex + drift - 1));
  return VISEMES[idx];
}
class MouthHoldGate {
  constructor(holdSeconds) {
    __publicField(this, "heldName", "idle");
    __publicField(this, "heldUntil", 0);
    this.holdSeconds = holdSeconds;
  }
  pick(now, candidate) {
    if (candidate === "idle") {
      this.heldName = "idle";
      this.heldUntil = 0;
      return "idle";
    }
    if (now >= this.heldUntil) {
      this.heldName = candidate;
      this.heldUntil = now + this.holdSeconds;
    }
    return this.heldName;
  }
  setHoldSeconds(next) {
    const clamped = Math.max(0, next);
    if (this.heldUntil > 0) {
      this.heldUntil = this.heldUntil - this.holdSeconds + clamped;
    }
    this.holdSeconds = clamped;
  }
}
class VisemeFadeScheduler {
  constructor(durationSec) {
    __publicField(this, "fromName", null);
    __publicField(this, "toName", null);
    __publicField(this, "elapsed", 0);
    __publicField(this, "durationSec");
    this.durationSec = Math.max(1e-4, durationSec);
  }
  setDuration(durationSec) {
    this.durationSec = Math.max(1e-4, durationSec);
  }
  /** Begin a new fade. Replaces any in-flight fade with the new target. */
  start(from, to) {
    this.fromName = from;
    this.toName = to;
    this.elapsed = 0;
  }
  /** Cancel any in-flight fade. Used on active→idle transitions. */
  cancel() {
    this.fromName = null;
    this.toName = null;
    this.elapsed = 0;
  }
  isActive() {
    return this.fromName !== null && this.toName !== null;
  }
  /**
   * Advance the fade by `delta` seconds. Returns the interpolation
   * factor and a `completed` flag. After the completed tick, callers
   * should swap their textures and call `cancel()` to release state.
   */
  tick(delta) {
    if (this.fromName === null || this.toName === null) return null;
    this.elapsed += delta;
    const t = Math.min(1, this.elapsed / this.durationSec);
    return {
      fromName: this.fromName,
      toName: this.toName,
      t,
      completed: t >= 1
    };
  }
}
function createSpeechAdapter(onAmplitude) {
  const synth = globalThis.speechSynthesis;
  const SSUCtor = globalThis.SpeechSynthesisUtterance;
  const supported = !!synth && !!SSUCtor;
  const TICK_MS = 16;
  let timerId = null;
  let cancelCurrent = null;
  const emit = (v) => {
    onAmplitude == null ? void 0 : onAmplitude(Math.max(0, Math.min(1, v)));
  };
  return {
    isSupported: supported,
    speakText(text) {
      if (!supported || !synth || !SSUCtor) return Promise.resolve();
      return new Promise((resolve) => {
        const utter = new SSUCtor(text);
        let elapsed = 0;
        const cleanup = () => {
          if (timerId !== null) clearInterval(timerId);
          timerId = null;
          emit(0);
          cancelCurrent = null;
          resolve();
        };
        utter.onend = cleanup;
        utter.onerror = cleanup;
        cancelCurrent = cleanup;
        let started = false;
        timerId = setInterval(() => {
          elapsed += TICK_MS;
          const t = elapsed / 1e3;
          if (!started && synth.speaking) started = true;
          if (started && !synth.speaking) {
            cleanup();
            return;
          }
          const base = 0.35 + 0.35 * Math.sin(2 * Math.PI * 4 * t);
          const jitter = 0.15 * Math.sin(2 * Math.PI * 11 * t + 0.7);
          emit(base + jitter);
        }, TICK_MS);
        synth.speak(utter);
      });
    },
    stop() {
      synth == null ? void 0 : synth.cancel();
      cancelCurrent == null ? void 0 : cancelCurrent();
    }
  };
}
const elementCaches = /* @__PURE__ */ new WeakMap();
function disconnectSafely(cache, analyser) {
  try {
    cache.source.disconnect(analyser);
  } catch {
  }
  try {
    analyser.disconnect();
  } catch {
  }
}
async function attachAudioElement(el, onRms) {
  const Ctor = globalThis.AudioContext ?? globalThis.webkitAudioContext;
  if (!Ctor) throw new Error("AudioContext unsupported in this environment");
  let cache = elementCaches.get(el);
  if (!cache) {
    const ctx = new Ctor();
    const source = ctx.createMediaElementSource(el);
    cache = { ctx, source, activeAnalyser: null };
    elementCaches.set(el, cache);
  }
  if (cache.ctx.state === "suspended") {
    try {
      await cache.ctx.resume();
    } catch (e) {
      console.warn("[audio-analyser] resume failed:", e.message ?? e);
    }
  }
  if (cache.ctx.state !== "running") {
    console.warn("[audio-analyser] context not running after resume — state:", cache.ctx.state);
  }
  if (cache.activeAnalyser) {
    console.warn("[audio-analyser] evicting active analyser before new attach", { hadActive: true });
    disconnectSafely(cache, cache.activeAnalyser);
    cache.activeAnalyser = null;
  }
  const analyser = cache.ctx.createAnalyser();
  analyser.fftSize = 256;
  cache.source.connect(analyser);
  analyser.connect(cache.ctx.destination);
  cache.activeAnalyser = analyser;
  const buf = new Uint8Array(analyser.fftSize);
  let rafId = null;
  let detached = false;
  let sawSignal = false;
  let firstTickAt = performance.now();
  const tick = () => {
    if (detached) return;
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / buf.length);
    if (rms > 0.01) sawSignal = true;
    if (!sawSignal && performance.now() - firstTickAt > 2e3) {
      console.warn("[audio-analyser] no RMS signal after 2 s of attach — ctx state:", cache.ctx.state);
      sawSignal = true;
    }
    onRms(Math.max(0, Math.min(1, rms)));
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
  return {
    detach() {
      if (detached) return;
      detached = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      disconnectSafely(cache, analyser);
      if (cache.activeAnalyser === analyser) {
        cache.activeAnalyser = null;
      }
    }
  };
}
function releaseAudioElement(el) {
  const cache = elementCaches.get(el);
  if (!cache) return;
  if (cache.activeAnalyser) {
    disconnectSafely(cache, cache.activeAnalyser);
    cache.activeAnalyser = null;
  }
}
function patchLoadedMaterials(root) {
  root.traverse((node) => {
    const mesh = node;
    if (!mesh.isMesh || !mesh.material) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of materials) {
      const mat = m;
      if (mat.transparent) {
        mat.transparent = false;
        mat.alphaTest = 0.5;
        mat.depthWrite = true;
        mat.needsUpdate = true;
      }
    }
  });
}
const MORPH_EXPRESSION_ALPHA_CUTOFF = 1e-3;
function isMorphFaceOverlayLayerName(name) {
  return /expression|hyojo|mouth|blink|facial|face.*alpha/i.test(name);
}
function isMorphCutoutLayerName(name) {
  return /eye[_-]?parts|eye[_-]?mask/i.test(name);
}
function isMorphShadowLayerName(name) {
  return /shadow/i.test(name);
}
function isMorphHairLayerName(name) {
  return /front[_-]?hair|back[_-]?hair|hair/i.test(name);
}
function materialSideToLabel(side) {
  if (side === THREE.BackSide) return "back";
  if (side === THREE.DoubleSide) return "double";
  return "front";
}
function materialSideFromLabel(side) {
  if (side === "front") return THREE.FrontSide;
  if (side === "back") return THREE.BackSide;
  if (side === "double") return THREE.DoubleSide;
  return void 0;
}
function autoMaterialRole(name, source) {
  if (isMorphFaceOverlayLayerName(name)) return "faceOverlay";
  if (isMorphCutoutLayerName(name)) return "cutout";
  if (isMorphHairLayerName(name)) return "hair";
  if (isMorphShadowLayerName(name)) return "shadow";
  const src = source;
  if (((src == null ? void 0 : src.alphaTest) ?? 0) > 0) return "cutout";
  return "auto";
}
function findMaterialOverride(overrides, meshName, materialName) {
  return overrides.find((entry) => entry.materialName === materialName && (!entry.meshName || entry.meshName === meshName));
}
function effectiveMaterialRole(meshName, materialName, source, overrides) {
  const override = findMaterialOverride(overrides, meshName, materialName);
  return (override == null ? void 0 : override.type) && override.type !== "auto" ? override.type : autoMaterialRole(materialName, source);
}
function applyRoleDefaults(material, role) {
  const mat = material;
  if (role === "opaque") {
    mat.transparent = false;
    mat.alphaTest = 0;
    mat.depthWrite = true;
    mat.depthTest = true;
    mat.polygonOffset = false;
    return;
  }
  if (role === "faceOverlay") {
    mat.transparent = true;
    mat.blending = THREE.NormalBlending;
    mat.depthWrite = false;
    mat.depthTest = true;
    mat.premultipliedAlpha = false;
    mat.alphaTest = Math.max(MORPH_EXPRESSION_ALPHA_CUTOFF, mat.alphaTest ?? 0);
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -1;
    mat.polygonOffsetUnits = -1;
    return;
  }
  if (role === "shadow") {
    mat.transparent = true;
    mat.depthWrite = false;
    mat.depthTest = true;
    mat.alphaTest = mat.alphaTest ?? 0;
    return;
  }
  if (role === "cutout") {
    mat.transparent = false;
    mat.alphaTest = Math.max(MORPH_EXPRESSION_ALPHA_CUTOFF, mat.alphaTest ?? 0.5);
    mat.depthWrite = true;
    mat.depthTest = true;
    return;
  }
  if (role === "hair") {
    mat.depthTest = true;
  }
}
function applyMaterialOverrideValues(mesh, material, override) {
  if (!override) return;
  const mat = material;
  if (override.transparent !== void 0) mat.transparent = override.transparent;
  if (override.alphaTest !== void 0) mat.alphaTest = override.alphaTest;
  if (override.depthWrite !== void 0) mat.depthWrite = override.depthWrite;
  if (override.depthTest !== void 0) mat.depthTest = override.depthTest;
  const side = materialSideFromLabel(override.side);
  if (side !== void 0) mat.side = side;
  if (override.renderOrder !== void 0) mesh.renderOrder = override.renderOrder;
  mat.needsUpdate = true;
}
function applyMaterialOverridesToModel(root, overrides) {
  if (!root) return;
  root.traverse((node) => {
    const mesh = node;
    if (!mesh.isMesh || !mesh.material) return;
    const meshName = mesh.name || "(mesh)";
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      const materialName = material.name || "(material)";
      const override = findMaterialOverride(overrides, meshName, materialName);
      const role = effectiveMaterialRole(meshName, materialName, material, overrides);
      applyRoleDefaults(material, role);
      applyMaterialOverrideValues(mesh, material, override);
    }
  });
}
function morphMaterialRenderOrder(material) {
  const materials = Array.isArray(material) ? material : [material];
  const names = materials.map((entry) => (entry == null ? void 0 : entry.name) || "").join(" ");
  if (isMorphHairLayerName(names)) return 40;
  if (isMorphCutoutLayerName(names)) return 35;
  if (isMorphFaceOverlayLayerName(names)) return 30;
  if (isMorphShadowLayerName(names)) return 20;
  if (/head/i.test(names)) return 10;
  return 0;
}
function makeMorphToonMaterial(source, meshName = "(mesh)", overrides = []) {
  const src = source;
  const name = (src == null ? void 0 : src.name) || "morph_toon_material";
  const override = findMaterialOverride(overrides, meshName, name);
  const role = effectiveMaterialRole(meshName, name, source, overrides);
  const faceOverlay = role === "faceOverlay";
  const cutout = role === "cutout";
  const shadow = role === "shadow";
  const opacity = (src == null ? void 0 : src.opacity) ?? 1;
  const transparent = faceOverlay || shadow || !!(src == null ? void 0 : src.transparent) || opacity < 1;
  const alphaTest = faceOverlay || cutout ? Math.max(MORPH_EXPRESSION_ALPHA_CUTOFF, (src == null ? void 0 : src.alphaTest) ?? 0) : (src == null ? void 0 : src.alphaTest) ?? 0;
  const material = new MeshBasicMaterial({
    name,
    map: (src == null ? void 0 : src.map) ?? null,
    alphaMap: (src == null ? void 0 : src.alphaMap) ?? null,
    color: (src == null ? void 0 : src.color) ? src.color.clone() : new Color(16777215),
    transparent,
    opacity,
    alphaTest,
    side: 2
  });
  material.depthWrite = !(faceOverlay || shadow || transparent && opacity < 1);
  material.depthTest = true;
  material.toneMapped = false;
  if (faceOverlay) {
    applyRoleDefaults(material, "faceOverlay");
  }
  applyRoleDefaults(material, role);
  applyMaterialOverrideValues({ renderOrder: 0 }, material, override);
  for (const tex of [material.map, material.alphaMap]) {
    if (!tex) continue;
    tex.colorSpace = SRGBColorSpace;
    tex.flipY = false;
    tex.needsUpdate = true;
  }
  return material;
}
function makeImportedCharacterToonMaterial(source, meshName = "(mesh)", overrides = []) {
  return makeMorphToonMaterial(source, meshName, overrides);
}
function applyImportedCharacterToonMaterials(root, overrides = []) {
  const materials = [];
  root.traverse((node) => {
    const mesh = node;
    if (!mesh.isMesh || !mesh.material) return;
    const meshName = mesh.name || "(mesh)";
    const nextMaterial = Array.isArray(mesh.material) ? mesh.material.map((mat) => makeImportedCharacterToonMaterial(mat, meshName, overrides)) : makeImportedCharacterToonMaterial(mesh.material, meshName, overrides);
    mesh.material = nextMaterial;
    mesh.renderOrder = morphMaterialRenderOrder(nextMaterial);
    for (const material of Array.isArray(nextMaterial) ? nextMaterial : [nextMaterial]) {
      const override = findMaterialOverride(overrides, meshName, material.name || "(material)");
      applyMaterialOverrideValues(mesh, material, override);
    }
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.frustumCulled = false;
    if (Array.isArray(nextMaterial)) materials.push(...nextMaterial);
    else materials.push(nextMaterial);
  });
  return materials;
}
function applyMorphToonMaterials(root, overrides = []) {
  const materials = [];
  root.traverse((node) => {
    const mesh = node;
    if (!mesh.isMesh || !mesh.material) return;
    const meshName = mesh.name || "(mesh)";
    const nextMaterial = Array.isArray(mesh.material) ? mesh.material.map((mat) => makeMorphToonMaterial(mat, meshName, overrides)) : makeMorphToonMaterial(mesh.material, meshName, overrides);
    mesh.material = nextMaterial;
    mesh.renderOrder = morphMaterialRenderOrder(nextMaterial);
    for (const material of Array.isArray(nextMaterial) ? nextMaterial : [nextMaterial]) {
      const override = findMaterialOverride(overrides, meshName, material.name || "(material)");
      applyMaterialOverrideValues(mesh, material, override);
    }
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.frustumCulled = false;
    if (Array.isArray(nextMaterial)) materials.push(...nextMaterial);
    else materials.push(nextMaterial);
  });
  return materials;
}
function shouldUseMorphToonMaterials(root) {
  let shouldUse = false;
  root.traverse((node) => {
    if (shouldUse) return;
    const mesh = node;
    if (!mesh.isMesh) return;
    if (mesh.morphTargetDictionary && Object.keys(mesh.morphTargetDictionary).length > 0) {
      shouldUse = true;
      return;
    }
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    const names = materials.map((entry) => (entry == null ? void 0 : entry.name) || "").join(" ");
    if (isMorphFaceOverlayLayerName(names) || isMorphCutoutLayerName(names)) {
      shouldUse = true;
    }
  });
  return shouldUse;
}
function textureIdentity(material) {
  var _a, _b;
  const mat = material;
  const image = (_a = mat.map) == null ? void 0 : _a.image;
  const imageName = typeof (image == null ? void 0 : image.name) === "string" ? image.name : "";
  const imageSrc = typeof (image == null ? void 0 : image.currentSrc) === "string" ? image.currentSrc : typeof (image == null ? void 0 : image.src) === "string" ? image.src : "";
  return [material.name, (_b = mat.map) == null ? void 0 : _b.name, imageName, imageSrc].filter(Boolean).join(" ").toLowerCase();
}
function materialHasTexture(material) {
  return !!material.map;
}
function shouldUseTexturePackMaterial(material, materialCount) {
  if (!materialHasTexture(material)) return true;
  if (materialCount <= 1) return true;
  const identity = textureIdentity(material);
  return /(^|[/\\_.\-\s])base($|[/\\_.\-\s])/.test(identity) || identity.includes("body") || identity.includes("face") || identity.includes("head") || identity.includes("skin");
}
function countUniqueMaterials(root) {
  const materials = /* @__PURE__ */ new Set();
  root.traverse((node) => {
    const mesh = node;
    if (!mesh.isMesh || !mesh.material) return;
    if (Array.isArray(mesh.material)) {
      for (const material of mesh.material) materials.add(material);
    } else {
      materials.add(mesh.material);
    }
  });
  return materials.size;
}
function textureDimension(value, key) {
  if (!value || typeof value !== "object") return null;
  const record = value;
  const direct = record[key];
  if (typeof direct === "number" && Number.isFinite(direct)) return direct;
  const natural = record[key === "width" ? "naturalWidth" : "naturalHeight"];
  if (typeof natural === "number" && Number.isFinite(natural)) return natural;
  const video = record[key === "width" ? "videoWidth" : "videoHeight"];
  if (typeof video === "number" && Number.isFinite(video)) return video;
  return null;
}
function collectModelTextureInfo(root) {
  const rows = [];
  const seen = /* @__PURE__ */ new Set();
  root.traverse((node) => {
    var _a;
    const mesh = node;
    if (!mesh.isMesh || !mesh.material) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      const mat = material;
      const textureName = ((_a = mat.map) == null ? void 0 : _a.name) || mat.name || "(no texture)";
      const row = {
        meshName: mesh.name || "(mesh)",
        materialName: mat.name || "(material)",
        textureName,
        width: mat.map ? textureDimension(mat.map.image, "width") : null,
        height: mat.map ? textureDimension(mat.map.image, "height") : null,
        materialType: autoMaterialRole(mat.name || "(material)", mat),
        transparent: mat.transparent === true,
        opacity: mat.opacity ?? 1,
        alphaTest: mat.alphaTest ?? 0,
        depthWrite: mat.depthWrite !== false,
        depthTest: mat.depthTest !== false,
        renderOrder: mesh.renderOrder ?? 0,
        side: materialSideToLabel(mat.side)
      };
      const key = `${row.meshName}
${row.materialName}
${row.textureName}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(row);
    }
  });
  return rows;
}
async function loadSceneObjectModel(url, format) {
  try {
    const manager = new LoadingManager();
    if (format === "fbx") {
      const fbx = await new Promise((resolve, reject) => {
        new FBXLoader(manager).load(
          url,
          (obj) => resolve(obj),
          void 0,
          (err) => reject(err instanceof Error ? err : new Error(String(err)))
        );
      });
      const root2 = fbx;
      patchLoadedMaterials(root2);
      root2.animations = fbx.animations ?? [];
      return root2;
    }
    const gltf = await new Promise((resolve, reject) => {
      const loader = new GLTFLoader(manager);
      const draco = new DRACOLoader();
      draco.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/");
      loader.setDRACOLoader(draco);
      loader.load(
        url,
        (g) => {
          draco.dispose();
          resolve(g);
        },
        void 0,
        (err) => {
          draco.dispose();
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      );
    });
    const root = gltf.scene;
    patchLoadedMaterials(root);
    root.animations = gltf.animations ?? [];
    return root;
  } catch (e) {
    console.warn("[model3d-shader] loadSceneObjectModel failed", { url, format, err: e.message });
    return null;
  }
}
function createRadialShadowTexture(doc) {
  const size = 256;
  const cv = doc.createElement("canvas");
  cv.width = size;
  cv.height = size;
  const ctx = cv.getContext("2d");
  if (!ctx) return new THREE.Texture();
  const c = size / 2;
  const g = ctx.createRadialGradient(c, c, 0, c, c, c);
  g.addColorStop(0, "rgba(0,0,0,0.62)");
  g.addColorStop(0.22, "rgba(0,0,0,0.46)");
  g.addColorStop(0.55, "rgba(0,0,0,0.24)");
  g.addColorStop(0.82, "rgba(0,0,0,0.07)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv);
  tex.needsUpdate = true;
  return tex;
}
function createBackdropTexture(doc, baseHex) {
  const top = new THREE.Color(baseHex).multiplyScalar(0.82);
  const bottom = new THREE.Color(baseHex).multiplyScalar(1.14);
  const S = 256;
  const cv = doc.createElement("canvas");
  cv.width = S;
  cv.height = S;
  const ctx = cv.getContext("2d");
  if (!ctx) return new THREE.Texture();
  const lg = ctx.createLinearGradient(0, 0, 0, S);
  lg.addColorStop(0, `#${top.getHexString()}`);
  lg.addColorStop(1, `#${bottom.getHexString()}`);
  ctx.fillStyle = lg;
  ctx.fillRect(0, 0, S, S);
  const rg = ctx.createRadialGradient(S / 2, S / 2, S * 0.16, S / 2, S / 2, S * 0.72);
  rg.addColorStop(0, "rgba(0,0,0,0)");
  rg.addColorStop(1, "rgba(0,0,0,0.34)");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, S, S);
  const glow = ctx.createRadialGradient(S / 2, S * 0.36, 0, S / 2, S * 0.36, S * 0.5);
  glow.addColorStop(0, "rgba(255,250,240,0.10)");
  glow.addColorStop(1, "rgba(255,250,240,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, S, S);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
const DEFAULT_PARAMS = {
  mouthFadeSpeed: 18,
  mouthOpacity: 1,
  blinkMinInterval: 2.6,
  blinkMaxInterval: 6,
  blinkDuration: 0.2,
  expressionFadeDuration: 0.35,
  visemeDriftRate: 12,
  mouthVisemeFadeDuration: 0.12,
  mouthHoldDuration: 0.16
};
const DEFAULT_FADE_IN = 0.22;
const ONE_SHOT_FADE_IN = 0.12;
const ONE_SHOT_FADE_OUT = 0.16;
const ONE_SHOT_FROM_DEFAULT = 0.16;
function createShaderComposed3DController(options = {}) {
  var _a;
  const editorPose = options.editorPose === true;
  const suppressAutoDefaultAnimation = options.suppressAutoDefaultAnimation === true;
  let container = null;
  let canvas = null;
  let renderer = null;
  let scene = null;
  let camera = null;
  let controls = null;
  let model = null;
  let materialOverrides = [];
  let contactShadow = null;
  let contactShadowBaseScale = 1;
  let groundOffsetY = 0;
  let stageControlled = false;
  let stageBaseYawDeg = 0;
  let headGazeBone = null;
  const headGazeTarget = { x: 0, y: 0 };
  const headGazeCur = { x: 0, y: 0 };
  const headGazeSaved = new Quaternion();
  const headGazeOffset = new Quaternion();
  const HEAD_GAZE_MAX_YAW = 0.2;
  const HEAD_GAZE_MAX_PITCH = 0.1;
  let gazePointerTs = -Infinity;
  let gazeWanderNextAt = 0;
  let gazeBiasOverride = null;
  let headGazeRollCur = 0;
  let patTimer = null;
  let patActive = false;
  const patBias = { x: 0, y: 0 };
  let patEyeStart = -10;
  let patEyeEnd = -10;
  let keyLight = null;
  let keyBaseIntensity = 2.2;
  let pokeSpring = { amp: 0, vel: 0 };
  const pokeSavedScale = new Vector3();
  let pokeCombo = createPokeComboState();
  let spinT = -1;
  const SPIN_SECONDS = 0.9;
  let mixer = null;
  let clips = [];
  const actions = /* @__PURE__ */ new Map();
  let defaultAction = null;
  let oneShotAction = null;
  let animationStopTimers = [];
  const attachedObjects = /* @__PURE__ */ new Map();
  let boneHintObjects = {};
  function clearBoneHint() {
    var _a2, _b, _c, _d, _e, _f;
    if (boneHintObjects.rafId !== void 0) {
      cancelAnimationFrame(boneHintObjects.rafId);
    }
    if (boneHintObjects.line && scene) scene.remove(boneHintObjects.line);
    if (boneHintObjects.sphere && scene) scene.remove(boneHintObjects.sphere);
    (_a2 = boneHintObjects.line) == null ? void 0 : _a2.geometry.dispose();
    (_c = (_b = boneHintObjects.line) == null ? void 0 : _b.material) == null ? void 0 : _c.dispose();
    (_d = boneHintObjects.sphere) == null ? void 0 : _d.geometry.dispose();
    (_f = (_e = boneHintObjects.sphere) == null ? void 0 : _e.material) == null ? void 0 : _f.dispose();
    boneHintObjects = {};
  }
  function setBoneHintForObject(objectId) {
    var _a2;
    clearBoneHint();
    if (objectId === null || !scene) return;
    const entry = attachedObjects.get(objectId);
    if (!entry || entry.spec.kind !== "dynamic") return;
    const boneName = (_a2 = entry.spec.attachment) == null ? void 0 : _a2.boneName;
    if (!boneName || !model) return;
    let bone = null;
    model.traverse((o) => {
      if (!bone && o.isBone && o.name === boneName) bone = o;
    });
    if (!bone) return;
    const sphereGeom = new THREE.SphereGeometry(0.03, 16, 16);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 1096065 });
    const sphereMesh = new THREE.Mesh(sphereGeom, sphereMat);
    scene.add(sphereMesh);
    const lineGeom = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const lineMat = new THREE.LineBasicMaterial({ color: 1096065, depthTest: false, transparent: true, opacity: 0.9 });
    const line = new THREE.Line(lineGeom, lineMat);
    line.renderOrder = 999;
    scene.add(line);
    boneHintObjects = { line, sphere: sphereMesh };
    const update = () => {
      var _a3;
      if (!boneHintObjects.line || !boneHintObjects.sphere || !scene) return;
      const boneWorld = new THREE.Vector3();
      bone.getWorldPosition(boneWorld);
      const objWorld = new THREE.Vector3();
      (_a3 = entry.loadedScene) == null ? void 0 : _a3.getWorldPosition(objWorld);
      boneHintObjects.sphere.position.copy(boneWorld);
      const positions = boneHintObjects.line.geometry.getAttribute("position");
      positions.setXYZ(0, boneWorld.x, boneWorld.y, boneWorld.z);
      positions.setXYZ(1, objWorld.x, objWorld.y, objWorld.z);
      positions.needsUpdate = true;
      boneHintObjects.rafId = requestAnimationFrame(update);
    };
    update();
  }
  function applyAiControlPreviewTint(obj) {
    obj.traverse((o) => {
      const mesh = o;
      if (!mesh.isMesh) return;
      const mat = mesh.material;
      if (!mat || !mat.clone) return;
      const cloned = mat.clone();
      cloned.transparent = true;
      cloned.opacity = 0.7;
      if ("emissive" in cloned && cloned.emissive) {
        cloned.emissive = new THREE.Color(4500223);
      }
      mesh.material = cloned;
    });
  }
  let transformGizmoCleanup = null;
  let activeTransformTool = null;
  const uniforms = createComposedUniforms();
  let texturePack = null;
  let pack = null;
  let morphPack = null;
  const morphRegistry = /* @__PURE__ */ new Map();
  const morphRefs = [];
  const controlledMorphBinds = [];
  let modelTextureInfo = [];
  const createCharacterMaterial = (skinning) => new ShaderMaterial({
    uniforms,
    vertexShader: composedCharacterVertexShader,
    fragmentShader: composedCharacterFragmentShader,
    side: 2,
    ...skinning ? { defines: { USE_SKINNING: "" } } : {}
  });
  const characterMaterial = createCharacterMaterial(true);
  const staticCharacterMaterial = createCharacterMaterial(false);
  let audioLevel = 0;
  let smoothedLevel = 0;
  let currentMouth = "idle";
  let mouthOpacity = DEFAULT_PARAMS.mouthOpacity;
  let mouthFadeSpeed = DEFAULT_PARAMS.mouthFadeSpeed;
  let visemeDriftRate = DEFAULT_PARAMS.visemeDriftRate;
  const mouthGate = new MouthHoldGate(DEFAULT_PARAMS.mouthHoldDuration);
  const mouthFadeScheduler = new VisemeFadeScheduler(DEFAULT_PARAMS.mouthVisemeFadeDuration);
  let currentMouthFade = null;
  let autoReturnToNature = options.autoReturnToNature !== false;
  let currentExpression = "Nature";
  let expressionTransition = null;
  let expressionFadeDuration = DEFAULT_PARAMS.expressionFadeDuration;
  let expressionDurations = {};
  let expressionAutoReturnTimer = null;
  let blinkMinInterval = DEFAULT_PARAMS.blinkMinInterval;
  let blinkMaxInterval = DEFAULT_PARAMS.blinkMaxInterval;
  let blinkDuration = DEFAULT_PARAMS.blinkDuration;
  let nextBlinkTime = 1.2;
  let blinkStart = -10;
  let currentBlinkWeight = 0;
  let currentMouthExpressionOverride = null;
  let currentBlinkExpressionOverride = null;
  let currentBlinkWeightOverride = null;
  let rafId = null;
  let elapsed = 0;
  let lastT = 0;
  const speechAdapter = createSpeechAdapter((v) => {
    audioLevel = v;
  });
  let audioHandle = null;
  let lastAttachedAudioEl = null;
  let currentState = "idle";
  const stateListeners = /* @__PURE__ */ new Set();
  const emitStateChange = (from, to) => {
    for (const l of stateListeners) l({ from, to, timestamp: Date.now() });
  };
  const prefersReducedMotion = typeof window !== "undefined" && !!((_a = window.matchMedia) == null ? void 0 : _a.call(window, "(prefers-reduced-motion: reduce)").matches);
  const getMorphExpression = (name) => {
    if (!morphPack) return null;
    if (name === "Nature" || name === "Neutral") return { id: "Nature", label: "Nature", binds: [], aliases: [] };
    return morphPack.expressions.find((e) => e.id === name || e.label === name || e.aliases.includes(name)) ?? null;
  };
  const morphExpressionKeys = (expr) => [
    expr.id,
    expr.label,
    expr.category,
    ...expr.aliases
  ].filter((value) => !!value).map((value) => value.trim().toLowerCase());
  const meshPathLeaf = (path) => {
    if (!path) return null;
    const parts = path.split(/[/.]/).map((part) => part.trim()).filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : null;
  };
  const morphTargetMatchesMesh = (target, meshPath) => {
    if (!meshPath) return true;
    const leaf = meshPathLeaf(meshPath);
    return target.meshName === meshPath || !!leaf && target.meshName === leaf || target.meshPath === meshPath || target.meshPath.endsWith(`/${meshPath}`) || target.meshPath.endsWith(`.${meshPath}`) || !!leaf && target.meshPath.endsWith(`/${leaf}`) || !!leaf && target.meshPath.endsWith(`.${leaf}`);
  };
  const morphTargetsByBlendShapeName = (name) => {
    const exact = morphRegistry.get(name);
    if (exact) return exact;
    const lowered = name.toLowerCase();
    for (const [key, targets] of morphRegistry) {
      if (key.toLowerCase() === lowered) return targets;
    }
    return [];
  };
  const nodePath = (node) => {
    const names = [];
    let cur = node;
    while (cur) {
      if (cur.name) names.unshift(cur.name);
      cur = cur.parent;
    }
    return names.join("/");
  };
  const resolveMorphBindTargets = (bind) => {
    let namedTargets = [];
    if (bind.blendShape) {
      namedTargets = morphTargetsByBlendShapeName(bind.blendShape);
    }
    if (namedTargets.length > 0) {
      const scoped = namedTargets.filter((target) => morphTargetMatchesMesh(target, bind.meshPath));
      if (scoped.length > 0) return scoped;
      return namedTargets;
    }
    if (bind.blendShapeIndex !== void 0) {
      const indexedTargets = morphRefs.filter((target) => target.index === bind.blendShapeIndex);
      const scoped = indexedTargets.filter((target) => morphTargetMatchesMesh(target, bind.meshPath));
      return scoped.length > 0 ? scoped : indexedTargets;
    }
    return [];
  };
  const clearControlledMorphTargets = () => {
    const seen = /* @__PURE__ */ new Set();
    for (const bind of controlledMorphBinds) {
      for (const target of resolveMorphBindTargets(bind)) {
        const key = `${target.mesh.uuid}:${target.index}`;
        if (seen.has(key)) continue;
        seen.add(key);
        target.mesh.morphTargetInfluences[target.index] = 0;
      }
    }
  };
  const applyMorphBind = (bind, weightOverride) => {
    const weight = weightOverride ?? bind.weight;
    const targets = resolveMorphBindTargets(bind);
    for (const target of targets) {
      target.mesh.morphTargetInfluences[target.index] = weight;
    }
  };
  const morphExpressionIdForViseme = (viseme) => {
    var _a2;
    const candidatesByViseme = {
      idle: [],
      A: ["aa", "a", "vrc.v_aa"],
      I: ["ih", "i", "vrc.v_ih"],
      U: ["ou", "u", "vrc.v_ou"],
      E: ["ee", "e", "vrc.v_ee"],
      O: ["oh", "o", "vrc.v_oh"]
    };
    const candidates = candidatesByViseme[viseme];
    if (candidates.length === 0 || !morphPack) return null;
    return ((_a2 = morphPack.expressions.find((expr) => {
      const keys = morphExpressionKeys(expr);
      return candidates.some((candidate) => keys.includes(candidate));
    })) == null ? void 0 : _a2.id) ?? null;
  };
  const morphMouthIntensity = () => {
    if (currentMouth === "idle") return 0;
    return MathUtils.clamp(smoothedLevel * 4.2, 0, 1) * mouthOpacity;
  };
  const morphVisemeWeights = () => {
    const intensity = morphMouthIntensity();
    if (intensity <= 1e-3) return {};
    const fade = currentMouthFade;
    if (fade && !fade.completed) {
      const weights = {};
      if (fade.fromName !== "idle") weights[fade.fromName] = (1 - fade.t) * intensity;
      if (fade.toName !== "idle") weights[fade.toName] = fade.t * intensity;
      return weights;
    }
    return { [currentMouth]: intensity };
  };
  const applyMorphExpression = (expression, multiplier = 1) => {
    for (const bind of expression.binds) applyMorphBind(bind, bind.weight * multiplier);
  };
  const blinkExpression = () => {
    if (!morphPack) return null;
    return morphPack.expressions.find((expr) => morphExpressionKeys(expr).includes("blink")) ?? null;
  };
  const isRestingExpression = (name) => {
    const normalized = name.trim().toLowerCase();
    return normalized === "nature" || normalized === "neutral";
  };
  const expressionBlocksBlink = () => expressionTransition !== null || !isRestingExpression(currentExpression);
  const applyMorphState = () => {
    if (!morphPack) return;
    clearControlledMorphTargets();
    const expression = getMorphExpression(currentExpression);
    if (expression) applyMorphExpression(expression);
    if (currentMouthExpressionOverride) {
      const mouthExpression = getMorphExpression(currentMouthExpressionOverride);
      if (mouthExpression) applyMorphExpression(mouthExpression, mouthOpacity);
    } else {
      const weights = morphVisemeWeights();
      for (const [viseme, weight] of Object.entries(weights)) {
        if (weight <= 0) continue;
        const mouthExpressionId = morphExpressionIdForViseme(viseme);
        const mouthExpression = mouthExpressionId ? getMorphExpression(mouthExpressionId) : null;
        if (mouthExpression) applyMorphExpression(mouthExpression, weight);
      }
    }
    const blinkWeight = expressionBlocksBlink() ? 0 : currentBlinkWeightOverride ?? currentBlinkWeight;
    const blink = blinkWeight > 0 ? currentBlinkExpressionOverride ? getMorphExpression(currentBlinkExpressionOverride) : blinkExpression() : null;
    if (blink) applyMorphExpression(blink, blinkWeight);
  };
  const registerMorphTargets = (root) => {
    morphRegistry.clear();
    morphRefs.length = 0;
    controlledMorphBinds.length = 0;
    root.traverse((node) => {
      const mesh = node;
      if (!mesh.isMesh || !mesh.morphTargetDictionary || !mesh.morphTargetInfluences) return;
      for (const [name, index] of Object.entries(mesh.morphTargetDictionary)) {
        mesh.morphTargetInfluences[index] = 0;
        const list = morphRegistry.get(name) ?? [];
        const ref = {
          mesh,
          meshName: mesh.name,
          meshPath: nodePath(mesh),
          index
        };
        list.push(ref);
        morphRefs.push(ref);
        morphRegistry.set(name, list);
      }
    });
    for (const expr of (morphPack == null ? void 0 : morphPack.expressions) ?? []) {
      for (const bind of expr.binds) {
        controlledMorphBinds.push(bind);
      }
    }
  };
  const getExpressionTexture = (name) => {
    if (!texturePack || !pack) return null;
    const entry = pack.expressions.find((e) => e.id === name);
    if (name === "Nature") {
      if ((entry == null ? void 0 : entry.url) === null) return uniforms.uMainTex.value;
      if (entry) return texturePack.expressions.get(name) ?? texturePack.eyeNormal;
      return texturePack.eyeNormal;
    }
    if (!entry) return null;
    if (entry.url === null) return uniforms.uMainTex.value;
    return texturePack.expressions.get(name) ?? null;
  };
  const getExpressionOpacity = (name) => {
    if (name !== "Nature") return 1;
    const entry = pack == null ? void 0 : pack.expressions.find((e) => e.id === "Nature");
    return (entry == null ? void 0 : entry.url) === null ? 0 : 1;
  };
  const applyExpressionLocal = (name) => {
    if (morphPack) {
      const expression = getMorphExpression(name);
      if (!expression) {
        console.warn(`[model3d-shader] setExpression: unknown morph id "${name}". Available:`, ["Nature", ...morphPack.expressions.map((e) => e.id)]);
        return;
      }
      if (expressionAutoReturnTimer !== null) {
        clearTimeout(expressionAutoReturnTimer);
        expressionAutoReturnTimer = null;
      }
      currentExpression = name === "Neutral" ? "Nature" : name;
      expressionTransition = null;
      applyMorphState();
      scheduleExpressionAutoReturn(currentExpression);
      return;
    }
    if (!pack) return;
    const isNature = name === "Nature";
    if (!isNature && !pack.expressions.some((e) => e.id === name)) {
      console.warn(`[model3d-shader] setExpression: unknown id "${name}". Available:`, ["Nature", ...pack.expressions.map((e) => e.id)]);
      return;
    }
    if (expressionAutoReturnTimer !== null) {
      clearTimeout(expressionAutoReturnTimer);
      expressionAutoReturnTimer = null;
    }
    if (name === currentExpression && !expressionTransition) {
      scheduleExpressionAutoReturn(name);
      return;
    }
    uniforms.uEyeTex.value = getExpressionTexture(currentExpression);
    uniforms.uEyeTexNext.value = getExpressionTexture(name);
    uniforms.uExpressionOpacityFrom.value = getExpressionOpacity(currentExpression);
    uniforms.uExpressionOpacityTo.value = getExpressionOpacity(name);
    uniforms.uExpressionMix.value = 0;
    uniforms.uEyeBlend.value = 0;
    if (expressionFadeDuration <= 0) {
      currentExpression = name;
      const tex = getExpressionTexture(name);
      uniforms.uEyeTex.value = tex;
      uniforms.uEyeTexNext.value = tex;
      uniforms.uExpressionOpacityFrom.value = getExpressionOpacity(name);
      uniforms.uExpressionOpacityTo.value = getExpressionOpacity(name);
      uniforms.uExpressionMix.value = 0;
      expressionTransition = null;
      scheduleExpressionAutoReturn(name);
      return;
    }
    expressionTransition = { target: name, elapsed: 0, duration: expressionFadeDuration };
    scheduleExpressionAutoReturn(name);
  };
  const scheduleExpressionAutoReturn = (name) => {
    if (expressionAutoReturnTimer !== null) {
      clearTimeout(expressionAutoReturnTimer);
      expressionAutoReturnTimer = null;
    }
    if (name === "Nature") return;
    const seconds = expressionDurations[name];
    if (typeof seconds !== "number" || seconds <= 0) return;
    expressionAutoReturnTimer = setTimeout(() => {
      expressionAutoReturnTimer = null;
      applyExpressionLocal("Nature");
    }, seconds * 1e3);
  };
  const frameModel = (object) => {
    if (!camera || !controls) return;
    const box = new Box3().setFromObject(object);
    const size = new Vector3();
    const centre = new Vector3();
    box.getSize(size);
    box.getCenter(centre);
    const maxAxis = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxAxis) || maxAxis <= 0) return;
    const scale = 2.25 / maxAxis;
    object.scale.setScalar(scale);
    object.position.set(-centre.x * scale, -box.min.y * scale + 5e-3, -centre.z * scale);
    groundOffsetY = object.position.y;
    if (contactShadow) {
      const footprintHalf = Math.max(size.x, size.z) * scale / 2;
      const radius = Math.min(1.9, Math.max(0.45, footprintHalf * 1.3));
      contactShadowBaseScale = radius / 1.6;
      contactShadow.scale.setScalar(contactShadowBaseScale);
    }
    controls.target.set(0, Math.min(1.1, size.y * scale * 0.55), 0);
    camera.position.set(0, controls.target.y + 0.25, 4.15);
    controls.update();
  };
  const keepCharacterAboveFloor = () => {
    if (!model) return;
    model.position.y = groundOffsetY;
    const box = new Box3().setFromObject(model);
    if (box.min.y < 5e-3) {
      groundOffsetY += 5e-3 - box.min.y;
      model.position.y = groundOffsetY;
    }
  };
  const updateMouth = (delta) => {
    const FALL_LAMBDA = 30;
    const lambda = audioLevel >= smoothedLevel ? mouthFadeSpeed : FALL_LAMBDA;
    smoothedLevel = MathUtils.damp(smoothedLevel, audioLevel, lambda, delta);
    const candidate = pickViseme(smoothedLevel, elapsed, visemeDriftRate);
    const held = mouthGate.pick(elapsed, candidate);
    if (held !== currentMouth) {
      if (held === "idle") {
        if (texturePack && mouthFadeScheduler.isActive()) {
          uniforms.uMouthTex.value = texturePack.visemes[currentMouth] ?? uniforms.uMouthTex.value;
        }
        mouthFadeScheduler.cancel();
        currentMouthFade = null;
        uniforms.uMouthMix.value = 0;
      } else if (currentMouth === "idle") {
        if (texturePack) {
          uniforms.uMouthTex.value = texturePack.visemes[held];
          uniforms.uMouthTexNext.value = texturePack.visemes[held];
        }
        mouthFadeScheduler.cancel();
        currentMouthFade = null;
        uniforms.uMouthMix.value = 0;
      } else {
        if (texturePack) uniforms.uMouthTexNext.value = texturePack.visemes[held];
        mouthFadeScheduler.start(currentMouth, held);
        currentMouthFade = { fromName: currentMouth, toName: held, t: 0, completed: false };
        uniforms.uMouthMix.value = 0;
      }
      currentMouth = held;
      if (morphPack) applyMorphState();
    }
    const fadeState = mouthFadeScheduler.tick(delta);
    currentMouthFade = fadeState;
    if (fadeState) {
      uniforms.uMouthMix.value = fadeState.t;
      if (fadeState.completed) {
        if (texturePack) uniforms.uMouthTex.value = texturePack.visemes[fadeState.toName];
        uniforms.uMouthMix.value = 0;
        mouthFadeScheduler.cancel();
        currentMouthFade = null;
      }
    }
    uniforms.uMouthBlend.value = currentMouth === "idle" ? 0 : 1;
    uniforms.uMouthOpacity.value = mouthOpacity;
    if (morphPack) applyMorphState();
  };
  const setBlinkWeight = (weight) => {
    uniforms.uEyeBlend.value = weight;
    if (!morphPack || Math.abs(currentBlinkWeight - weight) < 1e-3) return;
    currentBlinkWeight = weight;
    applyMorphState();
  };
  const updateBlink = (time) => {
    if (expressionBlocksBlink()) {
      setBlinkWeight(0);
      nextBlinkTime = Math.max(nextBlinkTime, time + 0.2);
      return;
    }
    if (currentBlinkExpressionOverride) {
      setBlinkWeight(currentBlinkWeightOverride ?? currentBlinkWeight);
      return;
    }
    const PAT_EYE = 0.85;
    if (patActive) {
      const a = Math.max(0, Math.min(1, (time - patEyeStart) / 0.35));
      setBlinkWeight(PAT_EYE * a);
      nextBlinkTime = Math.max(nextBlinkTime, time + 0.3);
      return;
    }
    if (time - patEyeEnd < 0.3) {
      const a = 1 - (time - patEyeEnd) / 0.3;
      setBlinkWeight(PAT_EYE * a);
      return;
    }
    if (time > nextBlinkTime) {
      blinkStart = time;
      const lo = Math.min(blinkMinInterval, blinkMaxInterval);
      const hi = Math.max(blinkMinInterval, blinkMaxInterval);
      nextBlinkTime = time + lo + Math.random() * (hi - lo);
    }
    const age = time - blinkStart;
    let blink = 0;
    if (age >= 0 && age < blinkDuration) {
      blink = Math.sin(age / blinkDuration * Math.PI);
    }
    setBlinkWeight(blink);
  };
  const updateExpressionTransition = (delta) => {
    if (!expressionTransition) return;
    expressionTransition.elapsed += delta;
    const t = Math.min(1, expressionTransition.elapsed / expressionTransition.duration);
    uniforms.uExpressionMix.value = MathUtils.smoothstep(t, 0, 1);
    if (t >= 1) {
      currentExpression = expressionTransition.target;
      const tex = getExpressionTexture(currentExpression);
      uniforms.uEyeTex.value = tex;
      uniforms.uEyeTexNext.value = tex;
      uniforms.uExpressionOpacityFrom.value = getExpressionOpacity(currentExpression);
      uniforms.uExpressionOpacityTo.value = getExpressionOpacity(currentExpression);
      uniforms.uExpressionMix.value = 0;
      expressionTransition = null;
    }
  };
  const proceduralById = /* @__PURE__ */ new Map();
  const objectBreathUniforms = [];
  const objectSwayUniforms = [];
  const tick = (nowMs) => {
    var _a2;
    if (!renderer || !scene || !camera) return;
    const dtMs = nowMs - lastT;
    lastT = nowMs;
    const delta = dtMs / 1e3;
    elapsed += delta;
    uniforms.uProcTime.value += delta;
    for (const u of objectBreathUniforms) u.uTime.value += delta;
    for (const u of objectSwayUniforms) u.uTime.value += delta;
    if (!editorPose) mixer == null ? void 0 : mixer.update(delta);
    for (const attached of attachedObjects.values()) {
      (_a2 = attached.mixer) == null ? void 0 : _a2.update(delta);
    }
    updateExpressionTransition(delta);
    updateMouth(delta);
    updateBlink(elapsed);
    if (morphPack) applyMorphState();
    if (!stageControlled) controls == null ? void 0 : controls.update();
    if (contactShadow && controls) {
      const t = Math.max(0, Math.min(1, Math.cos(controls.getPolarAngle())));
      contactShadow.material.opacity = 0.32 + 0.58 * t;
    }
    if (contactShadow && uniforms.uBreathEnabled.value > 0) {
      const breathPhase = Math.sin(uniforms.uProcTime.value * uniforms.uBreathSpeed.value);
      contactShadow.scale.setScalar(contactShadowBaseScale * (1 - 0.02 * breathPhase));
    }
    if (keyLight && !prefersReducedMotion) {
      keyLight.intensity = keyBaseIntensity * lightBreathFactor(elapsed);
    }
    const pokeActive = model && !stageControlled && !isPokeSettled(pokeSpring);
    if (pokeActive) pokeSpring = stepPokeSpring(pokeSpring, delta);
    let spinOffset = 0;
    if (spinT >= 0 && model && !stageControlled) {
      spinT += delta / SPIN_SECONDS;
      const p = Math.min(1, spinT);
      const eased = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      spinOffset = eased * Math.PI * 2;
      if (spinT >= 1) {
        spinT = -1;
        spinOffset = 0;
      }
    }
    const gazeActive = headGazeBone && !stageControlled && !editorPose;
    if (gazeActive) {
      if (gazeBiasOverride) {
        headGazeTarget.x = gazeBiasOverride.x;
        headGazeTarget.y = gazeBiasOverride.y;
      } else if (patActive) {
        headGazeTarget.x = patBias.x;
        headGazeTarget.y = patBias.y;
      } else {
        const nowT = performance.now();
        if (nowT - gazePointerTs > GAZE_WANDER_IDLE_MS && nowT >= gazeWanderNextAt) {
          const w = computeWanderTarget(Math.random);
          headGazeTarget.x = w.x;
          headGazeTarget.y = w.y;
          gazeWanderNextAt = nowT + nextWanderDelayMs(Math.random);
        }
      }
      const k = 1 - Math.exp(-6 * delta);
      headGazeCur.x += (headGazeTarget.x - headGazeCur.x) * k;
      headGazeCur.y += (headGazeTarget.y - headGazeCur.y) * k;
      const rollTarget = Math.max(-0.2, Math.min(
        0.2,
        (gazeBiasOverride == null ? void 0 : gazeBiasOverride.roll) ?? (patActive ? patBias.x * 0.35 : 0)
      ));
      headGazeRollCur += (rollTarget - headGazeRollCur) * k;
      headGazeSaved.copy(headGazeBone.quaternion);
      headGazeOffset.setFromEuler(new THREE.Euler(
        headGazeCur.y * HEAD_GAZE_MAX_PITCH,
        headGazeCur.x * HEAD_GAZE_MAX_YAW,
        headGazeRollCur,
        "YXZ"
      ));
      headGazeBone.quaternion.multiply(headGazeOffset);
    }
    if (pokeActive) {
      pokeSavedScale.copy(model.scale);
      const a = pokeSpring.amp;
      model.scale.set(
        pokeSavedScale.x * (1 + a * 0.05),
        pokeSavedScale.y * (1 - a * 0.06),
        pokeSavedScale.z * (1 + a * 0.05)
      );
    }
    if (spinOffset !== 0 && model) model.rotation.y += spinOffset;
    renderer.render(scene, camera);
    if (spinOffset !== 0 && model) model.rotation.y -= spinOffset;
    if (pokeActive) model.scale.copy(pokeSavedScale);
    if (gazeActive) headGazeBone.quaternion.copy(headGazeSaved);
    rafId = requestAnimationFrame(tick);
  };
  const resizeToContainer = () => {
    if (!container || !renderer || !camera) return;
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  const setupAnimations = (loadedClips) => {
    clips = loadedClips;
    if (!model || loadedClips.length === 0) return;
    mixer = new AnimationMixer(model);
    actions.clear();
    for (const clip of loadedClips) {
      actions.set(clip.name, mixer.clipAction(clip));
    }
    mixer.addEventListener("finished", (event) => {
      if (event.action !== oneShotAction) return;
      const outgoing = oneShotAction;
      if (!defaultAction || defaultAction === outgoing) {
        return;
      }
      oneShotAction = null;
      defaultAction.setLoop(LoopRepeat, Number.POSITIVE_INFINITY);
      defaultAction.clampWhenFinished = false;
      crossFadeAnimation(outgoing, defaultAction, ONE_SHOT_FADE_OUT);
    });
  };
  const scheduleStopAfterFade = (action, fadeSeconds) => {
    const timer = setTimeout(() => {
      animationStopTimers = animationStopTimers.filter((t) => t !== timer);
      if (action !== defaultAction && action !== oneShotAction) action.stop();
    }, Math.max(0, fadeSeconds * 1e3 + 80));
    animationStopTimers.push(timer);
  };
  const crossFadeAnimation = (from, to, fadeSeconds, resetToStart) => {
    to.reset();
    to.enabled = true;
    to.paused = false;
    to.setEffectiveWeight(1);
    to.play();
    if (from && from !== to) {
      from.enabled = true;
      from.crossFadeTo(to, fadeSeconds, false);
      scheduleStopAfterFade(from, fadeSeconds);
    } else {
      to.stopFading();
    }
    mixer == null ? void 0 : mixer.update(0);
    keepCharacterAboveFloor();
  };
  const playDefaultAnimation = (name) => {
    if (editorPose) return;
    const next = actions.get(name);
    if (!next) {
      console.warn(`[model3d-shader] playDefaultAnimation: clipName "${name}" not found. Available:`, [...actions.keys()]);
      return;
    }
    if (defaultAction === next && !oneShotAction) {
      next.enabled = true;
      next.paused = false;
      if (!next.isRunning()) next.play();
      return;
    }
    const previous = oneShotAction && oneShotAction !== next ? oneShotAction : defaultAction && defaultAction !== next ? defaultAction : null;
    defaultAction = next;
    next.setLoop(LoopRepeat, Number.POSITIVE_INFINITY);
    next.clampWhenFinished = false;
    oneShotAction = null;
    crossFadeAnimation(previous, next, DEFAULT_FADE_IN);
  };
  const playOneShotAnimationInternal = (name, count = 1) => {
    if (editorPose) return;
    const action = actions.get(name);
    if (!action) {
      console.warn(`[model3d-shader] playOneShotAnimation: clipName "${name}" not found. Available:`, [...actions.keys()]);
      return;
    }
    const previous = oneShotAction && oneShotAction !== action ? oneShotAction : defaultAction && defaultAction !== action ? defaultAction : null;
    oneShotAction = action;
    action.setLoop(LoopRepeat, count);
    action.clampWhenFinished = true;
    crossFadeAnimation(previous, action, previous === defaultAction ? ONE_SHOT_FROM_DEFAULT : ONE_SHOT_FADE_IN);
  };
  return {
    async mount(el, manifest) {
      const source = manifest;
      if (!source || typeof source.url !== "string") {
        throw new Error("shader-composed model3d runtime requires a manifest with { url: string, ... }");
      }
      const usesMorphTargets = source.expressionMode === "morphTargets" || !!source.morphPack;
      const hasPack = !!source.texturePack && !usesMorphTargets;
      if (hasPack) {
        const tp = source.texturePack;
        const baseFallback = typeof tp.base === "string" && tp.base.length > 0 ? tp.base : null;
        const fillSlot = (current) => {
          if (typeof current === "string" && current.length > 0) return current;
          return baseFallback ?? current;
        };
        tp.eyeMask = fillSlot(tp.eyeMask);
        tp.eyeNormal = fillSlot(tp.eyeNormal);
        tp.eyeBlink = fillSlot(tp.eyeBlink);
        tp.mouthMask = fillSlot(tp.mouthMask);
        if (!tp.visemes || typeof tp.visemes !== "object") tp.visemes = {};
        const v = tp.visemes;
        v.idle = fillSlot(v.idle);
        v.A = fillSlot(v.A);
        v.E = fillSlot(v.E);
        v.I = fillSlot(v.I);
        v.O = fillSlot(v.O);
        v.U = fillSlot(v.U);
        if (!Array.isArray(tp.expressions)) tp.expressions = [];
      }
      morphPack = source.morphPack ?? null;
      materialOverrides = Array.isArray(source.materialOverrides) ? source.materialOverrides : [];
      container = el;
      pack = hasPack ? source.texturePack ?? null : null;
      canvas = el.ownerDocument.createElement("canvas");
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      canvas.style.display = "block";
      el.appendChild(canvas);
      const transparent = options.transparent === true;
      renderer = new WebGLRenderer({
        canvas,
        antialias: true,
        alpha: transparent,
        premultipliedAlpha: !transparent
      });
      if (transparent) {
        renderer.setClearColor(new Color("#000000"), 0);
      } else {
        renderer.setClearColor(new Color(options.backgroundColor ?? "#11161a"), 1);
      }
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.outputColorSpace = SRGBColorSpace;
      scene = new Scene();
      if (!transparent) {
        scene.background = createBackdropTexture(el.ownerDocument, options.backgroundColor ?? "#11161a");
      }
      camera = new PerspectiveCamera(35, 1, 0.01, 100);
      camera.position.set(0, 1.35, 4.25);
      controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true;
      controls.target.set(0, 0.9, 0);
      controls.minDistance = 1.2;
      controls.maxDistance = 8;
      controls.enablePan = false;
      controls.maxPolarAngle = Math.PI * 0.52;
      const now = /* @__PURE__ */ new Date();
      const tod = hourToLighting(now.getHours() + now.getMinutes() / 60);
      const key = new DirectionalLight(tod.key, tod.keyIntensity);
      key.position.set(2.5, 4, 3);
      scene.add(key);
      keyLight = key;
      keyBaseIntensity = tod.keyIntensity;
      scene.add(new HemisphereLight(tod.sky, tod.ground, tod.hemiIntensity));
      const rim = new DirectionalLight(tod.rim, tod.rimIntensity);
      rim.position.set(-2.2, 3.4, -3.6);
      scene.add(rim);
      if (!transparent) {
        const floor = new Mesh(
          new CircleGeometry(1.6, 96),
          new MeshBasicMaterial({
            map: createRadialShadowTexture(el.ownerDocument),
            transparent: true,
            depthWrite: false,
            opacity: 0.9
          })
        );
        floor.rotation.x = -Math.PI / 2;
        floor.position.set(-0.13, -0.015, -0.15);
        floor.renderOrder = -1;
        scene.add(floor);
        contactShadow = floor;
      }
      resizeToContainer();
      if (hasPack) {
        texturePack = await loadTexturePack(source.texturePack);
        uniforms.uMainTex.value = texturePack.base;
        uniforms.uEyeMask.value = texturePack.eyeMask;
        uniforms.uCloseEyeTex.value = texturePack.eyeBlink;
        uniforms.uMouthMask.value = texturePack.mouthMask;
        uniforms.uMouthTex.value = texturePack.visemes.idle;
        uniforms.uMouthTexNext.value = texturePack.visemes.idle;
        uniforms.uMouthMix.value = 0;
        const initialExpression = source.defaultExpression ?? "Nature";
        currentExpression = initialExpression;
        const initTex = getExpressionTexture(initialExpression);
        uniforms.uEyeTex.value = initTex;
        uniforms.uEyeTexNext.value = initTex;
        uniforms.uExpressionOpacityFrom.value = getExpressionOpacity(initialExpression);
        uniforms.uExpressionOpacityTo.value = getExpressionOpacity(initialExpression);
        uniforms.uExpressionMix.value = 0;
      }
      const ext = source.url.toLowerCase().split("?")[0].split("#")[0];
      const fmt = source.format ?? (ext.endsWith(".fbx") ? "fbx" : "glb");
      const manager = new LoadingManager();
      let loadedClips = [];
      if (fmt === "fbx") {
        const fbxRoot = await new Promise((resolve, reject) => {
          new FBXLoader(manager).load(
            source.url,
            (obj) => resolve(obj),
            void 0,
            (err) => reject(err instanceof Error ? err : new Error(String(err)))
          );
        });
        model = fbxRoot;
        loadedClips = fbxRoot.animations ?? [];
      } else {
        const gltf = await new Promise((resolve, reject) => {
          const loader = new GLTFLoader(manager);
          const draco = new DRACOLoader();
          draco.setDecoderPath("https://www.gstatic.com/draco/v1/decoders/");
          loader.setDRACOLoader(draco);
          loader.load(
            source.url,
            (g) => {
              draco.dispose();
              resolve(g);
            },
            void 0,
            (err) => {
              draco.dispose();
              reject(err instanceof Error ? err : new Error(String(err)));
            }
          );
        });
        model = gltf.scene;
        loadedClips = gltf.animations ?? [];
      }
      modelTextureInfo = collectModelTextureInfo(model);
      const useMorphToonMaterials = !!morphPack || shouldUseMorphToonMaterials(model);
      if (useMorphToonMaterials) {
        registerMorphTargets(model);
      }
      if (morphPack) {
        currentExpression = source.defaultExpression ?? "Nature";
        applyMorphState();
      }
      if (hasPack && texturePack && true) {
        const materialCount = countUniqueMaterials(model);
        model.traverse((node) => {
          const mesh = node;
          if (mesh.isMesh && mesh.material) {
            const composedMaterial = mesh.isSkinnedMesh ? characterMaterial : staticCharacterMaterial;
            const convertMaterial = (material) => {
              if (!shouldUseTexturePackMaterial(material, materialCount)) return material;
              return composedMaterial;
            };
            mesh.material = Array.isArray(mesh.material) ? mesh.material.map(convertMaterial) : convertMaterial(mesh.material);
            mesh.castShadow = false;
            mesh.receiveShadow = false;
            mesh.frustumCulled = false;
          }
        });
        uniforms.uMainTex.value = texturePack.base;
        const initialExpression = currentExpression;
        const eyeTex = getExpressionTexture(initialExpression);
        uniforms.uEyeTex.value = eyeTex;
        uniforms.uEyeTexNext.value = eyeTex;
      } else if (useMorphToonMaterials) {
        const morphMaterials = applyMorphToonMaterials(model, materialOverrides);
        const procActionsMorph = source.proceduralActions ?? [];
        const activeBreathMorph = prefersReducedMotion ? void 0 : procActionsMorph.find(
          (a) => a.type === "breath" && a.enabled
        );
        if (activeBreathMorph) {
          const procBox = new Box3().setFromObject(model);
          const procSize = new Vector3();
          procBox.getSize(procSize);
          const sharedUniforms = createBreathUniforms(
            activeBreathMorph,
            { minY: procBox.min.y, height: Math.max(procSize.y, 1e-4) },
            { value: 0 }
          );
          for (const mat of morphMaterials) applyBreathShader(mat, sharedUniforms);
          const list = proceduralById.get(activeBreathMorph.id) ?? [];
          list.push(sharedUniforms);
          proceduralById.set(activeBreathMorph.id, list);
          objectBreathUniforms.push(sharedUniforms);
        } else {
          const activeSwayMorph = prefersReducedMotion ? void 0 : procActionsMorph.find(
            (a) => a.type === "sway" && a.enabled
          );
          if (activeSwayMorph) {
            const procBox = new Box3().setFromObject(model);
            const procSize = new Vector3();
            procBox.getSize(procSize);
            const swayUniforms = createSwayUniforms(
              activeSwayMorph,
              { minY: procBox.min.y, height: Math.max(procSize.y, 1e-4) },
              { value: 0 }
            );
            for (const mat of morphMaterials) applySwayShader(mat, swayUniforms);
            objectSwayUniforms.push(swayUniforms);
          }
        }
      } else {
        applyImportedCharacterToonMaterials(model, materialOverrides);
        model.traverse((node) => {
          const mesh = node;
          if (mesh.isMesh) {
            mesh.castShadow = false;
            mesh.receiveShadow = false;
            mesh.frustumCulled = false;
          }
        });
      }
      applyMaterialOverridesToModel(model, materialOverrides);
      if (!scene || !renderer) {
        model = null;
        return;
      }
      frameModel(model);
      scene.add(model);
      setupAnimations(loadedClips);
      model.visible = true;
      headGazeBone = null;
      headGazeCur.x = 0;
      headGazeCur.y = 0;
      headGazeTarget.x = 0;
      headGazeTarget.y = 0;
      if (!prefersReducedMotion) {
        model.traverse((o) => {
          if (!headGazeBone && o.isBone && o.name.toLowerCase().endsWith("head")) {
            headGazeBone = o;
          }
        });
      }
      const procActions = source.proceduralActions ?? [];
      const activeBreath = prefersReducedMotion ? void 0 : procActions.find(
        (a) => a.type === "breath" && a.enabled
      );
      if (activeBreath && model && true) {
        const procBox = new Box3().setFromObject(model);
        const procSize = new Vector3();
        procBox.getSize(procSize);
        uniforms.uBreathEnabled.value = 1;
        uniforms.uBreathStrength.value = activeBreath.params.strength;
        uniforms.uBreathSpeed.value = activeBreath.params.speed;
        uniforms.uBreathMaskStart.value = activeBreath.params.maskStart;
        uniforms.uBreathMaskSoftness.value = activeBreath.params.maskSoftness;
        uniforms.uBreathModelMinY.value = procBox.min.y;
        uniforms.uBreathModelHeight.value = Math.max(procSize.y, 1e-4);
        const ax = activeBreath.params.axis;
        uniforms.uBreathAxis.value.set(ax === "x" ? 1 : 0, ax === "y" ? 1 : 0, ax === "z" ? 1 : 0);
        proceduralById.set(activeBreath.id, [{
          uTime: uniforms.uProcTime,
          uBreathEnabled: uniforms.uBreathEnabled,
          uBreathStrength: uniforms.uBreathStrength,
          uBreathSpeed: uniforms.uBreathSpeed,
          uMaskStart: uniforms.uBreathMaskStart,
          uMaskSoftness: uniforms.uBreathMaskSoftness,
          uModelMinY: uniforms.uBreathModelMinY,
          uModelHeight: uniforms.uBreathModelHeight,
          axis: ax
        }]);
      }
      const activeSway = prefersReducedMotion ? void 0 : procActions.find(
        (a) => a.type === "sway" && a.enabled
      );
      if (activeSway && model && true) {
        const swBox = new Box3().setFromObject(model);
        const swSize = new Vector3();
        swBox.getSize(swSize);
        uniforms.uSwayEnabled.value = 1;
        uniforms.uSwayAmplitude.value = activeSway.params.amplitude;
        uniforms.uSwaySpeed.value = activeSway.params.speed;
        uniforms.uSwayMaskStart.value = activeSway.params.maskStart;
        uniforms.uSwayMaskSoftness.value = activeSway.params.maskSoftness;
        uniforms.uSwayModelMinY.value = swBox.min.y;
        uniforms.uSwayModelHeight.value = Math.max(swSize.y, 1e-4);
        const sax = activeSway.params.axis;
        uniforms.uSwayAxis.value.set(sax === "x" ? 1 : 0, 0, sax === "z" ? 1 : 0);
      }
      const wantedDefault = source.defaultAnimation ?? (loadedClips[0] ? loadedClips[0].name : null);
      if (!editorPose && !suppressAutoDefaultAnimation && wantedDefault && actions.has(wantedDefault)) {
        playDefaultAnimation(wantedDefault);
      }
      if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(() => resizeToContainer());
        ro.observe(el);
        el.__m3dResizeObserver = ro;
      }
      if (!prefersReducedMotion && !editorPose) {
        const onGazeMove = (e) => {
          const r = el.getBoundingClientRect();
          if (r.width <= 0 || r.height <= 0) return;
          gazePointerTs = performance.now();
          if (pokeDown && Math.hypot(e.clientX - pokeDown.x, e.clientY - pokeDown.y) > 6) {
            if (patTimer !== null) {
              clearTimeout(patTimer);
              patTimer = null;
            }
            patActive = false;
          }
          headGazeTarget.x = Math.max(-1, Math.min(1, (e.clientX - r.left) / r.width * 2 - 1));
          headGazeTarget.y = Math.max(-1, Math.min(1, (e.clientY - r.top) / r.height * 2 - 1));
        };
        const onGazeLeave = () => {
          headGazeTarget.x = 0;
          headGazeTarget.y = 0;
        };
        const pokeRaycaster = new THREE.Raycaster();
        const pokeNdc = new THREE.Vector2();
        let pokeDown = null;
        const onPokeDown = (e) => {
          pokeDown = { x: e.clientX, y: e.clientY, t: performance.now() };
          if (patTimer !== null) clearTimeout(patTimer);
          patTimer = setTimeout(() => {
            patTimer = null;
            if (!model || !camera || stageControlled || !pokeDown) return;
            const r = el.getBoundingClientRect();
            if (r.width <= 0 || r.height <= 0) return;
            pokeNdc.set(
              (pokeDown.x - r.left) / r.width * 2 - 1,
              -((pokeDown.y - r.top) / r.height * 2 - 1)
            );
            pokeRaycaster.setFromCamera(pokeNdc, camera);
            if (pokeRaycaster.intersectObject(model, true).length === 0) return;
            patActive = true;
            patEyeStart = elapsed;
            patBias.x = Math.max(-0.45, Math.min(0.45, pokeNdc.x * 0.45));
            patBias.y = 0.3;
            el.dispatchEvent(new CustomEvent("nx-character-pat", {
              bubbles: true,
              detail: { clientX: pokeDown.x, clientY: pokeDown.y }
            }));
          }, 500);
        };
        const onPokeUp = (e) => {
          if (patTimer !== null) {
            clearTimeout(patTimer);
            patTimer = null;
          }
          const wasPat = patActive;
          patActive = false;
          if (wasPat) patEyeEnd = elapsed;
          const d = pokeDown;
          pokeDown = null;
          if (wasPat) return;
          if (!d || !model || !camera || stageControlled) return;
          if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6) return;
          if (performance.now() - d.t > 300) return;
          const r = el.getBoundingClientRect();
          if (r.width <= 0 || r.height <= 0) return;
          pokeNdc.set(
            (e.clientX - r.left) / r.width * 2 - 1,
            -((e.clientY - r.top) / r.height * 2 - 1)
          );
          pokeRaycaster.setFromCamera(pokeNdc, camera);
          if (pokeRaycaster.intersectObject(model, true).length > 0) {
            pokeSpring = pokeImpulse(pokeSpring);
            el.dispatchEvent(new CustomEvent("nx-character-poke", {
              bubbles: true,
              detail: { clientX: e.clientX, clientY: e.clientY }
            }));
            const combo = registerPoke(pokeCombo, performance.now());
            pokeCombo = combo.state;
            if (combo.triggered && spinT < 0) {
              spinT = 0;
              pokeSpring = pokeImpulse(pokeSpring, 1.5);
              el.dispatchEvent(new CustomEvent("nx-character-tickle", {
                bubbles: true,
                detail: { clientX: e.clientX, clientY: e.clientY }
              }));
            }
          }
        };
        el.addEventListener("pointermove", onGazeMove);
        el.addEventListener("pointerleave", onGazeLeave);
        el.addEventListener("pointerdown", onPokeDown);
        el.addEventListener("pointerup", onPokeUp);
        el.__m3dGazeCleanup = () => {
          el.removeEventListener("pointermove", onGazeMove);
          el.removeEventListener("pointerleave", onGazeLeave);
          el.removeEventListener("pointerdown", onPokeDown);
          el.removeEventListener("pointerup", onPokeUp);
          if (patTimer !== null) {
            clearTimeout(patTimer);
            patTimer = null;
          }
          patActive = false;
        };
      }
      lastT = performance.now();
      rafId = requestAnimationFrame(tick);
    },
    setState(name) {
      if (name === currentState) return;
      const from = currentState;
      currentState = name;
      const map = options.animationMap;
      const clip = map == null ? void 0 : map[name];
      if (clip && actions.has(clip)) {
        const oneShots = options.oneShotClips ?? [];
        if (oneShots.includes(clip)) playOneShotAnimationInternal(clip);
        else playDefaultAnimation(clip);
      }
      emitStateChange(from, name);
    },
    trigger(_event) {
    },
    async attachAudio(source) {
      if (source instanceof HTMLAudioElement) {
        audioHandle == null ? void 0 : audioHandle.detach();
        lastAttachedAudioEl = source;
        audioHandle = await attachAudioElement(source, (rms) => {
          audioLevel = rms;
        });
        return;
      }
      console.warn("[model3d-shader] attachAudio(MediaStream) not yet supported");
    },
    // ?????scene-objects-and-attachments ???????????????????????????????????????????????????????????
    getAiControlledSceneObjectIds() {
      const ids = [];
      for (const [id, entry] of attachedObjects) {
        if (entry.spec.visibility === "ai-controlled") ids.push(id);
      }
      return ids;
    },
    setSceneObjectBoneHint(objectId) {
      setBoneHintForObject(objectId);
    },
    getAvailableBones() {
      if (!model) return [];
      const names = [];
      model.traverse((o) => {
        if (o.isBone) names.push(o.name);
      });
      return Array.from(new Set(names)).sort();
    },
    setGazeBias(bias) {
      gazeBiasOverride = bias ? {
        x: Math.max(-1, Math.min(1, bias.x)),
        y: Math.max(-1, Math.min(1, bias.y)),
        roll: bias.roll === void 0 ? void 0 : Math.max(-0.2, Math.min(0.2, bias.roll))
      } : null;
    },
    nudge(strength = 0.8) {
      if (prefersReducedMotion || stageControlled) return;
      pokeSpring = pokeImpulse(pokeSpring, Math.max(0.2, Math.min(2, strength)));
    },
    // stage-livestudio ??additive root-transform control. Writes only
    // the loaded model root's x/z position and Y rotation; everything
    // else (shader, breath, visemes, scene objects) is untouched.
    setStagePosition(x, z) {
      if (!model) return;
      model.position.x = x;
      model.position.z = z;
    },
    setStageFacing(deg) {
      if (!model) return;
      model.rotation.y = MathUtils.degToRad(deg + stageBaseYawDeg);
    },
    setStageYOffset(y) {
      if (!model) return;
      model.position.y = groundOffsetY + Math.max(0, y);
    },
    getStagePosition() {
      if (!model) return null;
      return [model.position.x, model.position.z];
    },
    setCharacterVisible(visible) {
      if (model) model.visible = visible;
    },
    setStageHeight(y) {
      if (model) model.position.y = y;
    },
    getAllClipNames() {
      return clips.map((c) => c.name);
    },
    getThreeContext() {
      if (!scene || !camera || !renderer) return null;
      return { scene, camera, renderer, controls };
    },
    getModelRoot() {
      return model;
    },
    setStageControlled(enabled) {
      stageControlled = enabled;
      if (controls) controls.enabled = !enabled;
    },
    async attachSceneObject(spec) {
      var _a2, _b;
      if (!scene || !model) {
        console.warn("[model3d-shader] attachSceneObject: scene/model not ready");
        return;
      }
      const existing = attachedObjects.get(spec.id);
      if (existing == null ? void 0 : existing.loadedScene) return;
      const myToken = Symbol(`scene-object-${spec.id}`);
      attachedObjects.set(spec.id, {
        spec,
        loadedScene: null,
        mixer: null,
        collider: null,
        token: myToken
      });
      const loaded = await loadSceneObjectModel(spec.modelPath, spec.modelFormat);
      const current = attachedObjects.get(spec.id);
      if (!current || current.token !== myToken) {
        return;
      }
      if (!loaded) {
        attachedObjects.delete(spec.id);
        return;
      }
      loaded.position.set(...spec.transform.position);
      loaded.rotation.set(...spec.transform.rotation);
      loaded.scale.set(...spec.transform.scale);
      const previewForceShow = options.previewMode === true && spec.visibility === "ai-controlled";
      loaded.visible = spec.visibility === "always" || previewForceShow;
      if (previewForceShow) {
        applyAiControlPreviewTint(loaded);
      }
      if (!scene || !model) {
        return;
      }
      if (spec.kind === "static") {
        scene.add(loaded);
      } else {
        const boneName = (_a2 = spec.attachment) == null ? void 0 : _a2.boneName;
        if (!boneName) {
          console.warn("[model3d-shader] sceneObject: dynamic without attachment.boneName, skipping", spec.id);
          attachedObjects.delete(spec.id);
          return;
        }
        let bone = null;
        model.traverse((o) => {
          if (!bone && o.isBone && o.name === boneName) bone = o;
        });
        if (!bone) {
          console.warn(`[model3d-shader] sceneObject: bone "${boneName}" not found ??skipping attach`, { id: spec.id });
          attachedObjects.delete(spec.id);
          return;
        }
        bone.add(loaded);
      }
      let objMixer = null;
      const objAnimations = loaded.animations ?? [];
      if (objAnimations.length > 0) {
        objMixer = new AnimationMixer(loaded);
        objMixer.clipAction(objAnimations[0]).play();
      }
      const box = new Box3().setFromObject(loaded);
      const collider = {
        min: [box.min.x, box.min.y, box.min.z],
        max: [box.max.x, box.max.y, box.max.z]
      };
      if (spec.texture) {
        try {
          const tex = await new TextureLoader().loadAsync(spec.texture);
          tex.colorSpace = SRGBColorSpace;
          tex.flipY = false;
          loaded.traverse((node) => {
            if (!node.isMesh) return;
            const mesh = node;
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const mat of mats) {
              if (!mat) continue;
              const m = mat;
              m.map = tex;
              m.needsUpdate = true;
            }
          });
        } catch (err) {
          console.warn("[model3d-shader] sceneObject: texture override load failed", { id: spec.id, url: spec.texture, err });
        }
      }
      const procActions = (prefersReducedMotion ? [] : spec.proceduralActions ?? []).filter(
        (a) => a.type === "breath" && a.enabled
      );
      if (procActions.length > 0) {
        const objBox = new Box3().setFromObject(loaded);
        const objSize = new Vector3();
        objBox.getSize(objSize);
        const worldMinY = objBox.min.y;
        const worldHeight = Math.max(objSize.y, 1e-4);
        loaded.traverse((node) => {
          if (!node.isMesh) return;
          const mesh = node;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mat of mats) {
            if (!mat) continue;
            for (const ba of procActions) {
              const uniforms2 = createBreathUniforms(
                ba,
                { minY: worldMinY, height: worldHeight },
                { value: 0 }
                // each object gets its own time ref advanced in tick()
              );
              applyBreathShader(mat, uniforms2);
              const list = proceduralById.get(ba.id) ?? [];
              list.push(uniforms2);
              proceduralById.set(ba.id, list);
              objectBreathUniforms.push(uniforms2);
            }
          }
        });
      } else {
        const swayActions = (prefersReducedMotion ? [] : spec.proceduralActions ?? []).filter(
          (a) => a.type === "sway" && a.enabled
        );
        if (swayActions.length > 0) {
          const objBox = new Box3().setFromObject(loaded);
          const objSize = new Vector3();
          objBox.getSize(objSize);
          const worldMinY = objBox.min.y;
          const worldHeight = Math.max(objSize.y, 1e-4);
          loaded.traverse((node) => {
            if (!node.isMesh) return;
            const mesh = node;
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const mat of mats) {
              if (!mat) continue;
              for (const sa of swayActions) {
                const uniforms2 = createSwayUniforms(sa, { minY: worldMinY, height: worldHeight }, { value: 0 });
                applySwayShader(mat, uniforms2);
                objectSwayUniforms.push(uniforms2);
              }
            }
          });
        }
      }
      const finalCheck = attachedObjects.get(spec.id);
      if ((finalCheck == null ? void 0 : finalCheck.token) === myToken) {
        attachedObjects.set(spec.id, { spec, loadedScene: loaded, mixer: objMixer, collider, token: myToken });
      } else {
        (_b = loaded.parent) == null ? void 0 : _b.remove(loaded);
        objMixer == null ? void 0 : objMixer.stopAllAction();
      }
    },
    async setSceneObjectTexture(id, url) {
      const attached = attachedObjects.get(id);
      const loaded = attached == null ? void 0 : attached.loadedScene;
      if (!loaded) return;
      if (!url) {
        return;
      }
      try {
        const tex = await new TextureLoader().loadAsync(url);
        tex.colorSpace = SRGBColorSpace;
        tex.flipY = false;
        loaded.traverse((node) => {
          if (!node.isMesh) return;
          const mesh = node;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mat of mats) {
            if (!mat) continue;
            const m = mat;
            m.map = tex;
            m.needsUpdate = true;
          }
        });
      } catch (err) {
        console.warn("[model3d-shader] setSceneObjectTexture failed", { id, url, err });
      }
    },
    setSceneObjectProceduralActions(id, actions2) {
      const attached = attachedObjects.get(id);
      const loaded = attached == null ? void 0 : attached.loadedScene;
      if (!loaded) return;
      const breaths = actions2.filter((a) => a.type === "breath" && a.enabled);
      const box = new Box3().setFromObject(loaded);
      const sz = new Vector3();
      box.getSize(sz);
      const worldMinY = box.min.y;
      const worldHeight = Math.max(sz.y, 1e-4);
      loaded.traverse((node) => {
        var _a2;
        if (!node.isMesh) return;
        const mesh = node;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const mat of mats) {
          if (!mat) continue;
          if (breaths.length === 0) {
            const u = (_a2 = mat.userData) == null ? void 0 : _a2.proceduralBreath;
            if (u) u.uBreathEnabled.value = 0;
            continue;
          }
          for (const ba of breaths) {
            const uniforms2 = createBreathUniforms(
              ba,
              { minY: worldMinY, height: worldHeight },
              { value: 0 }
            );
            applyBreathShader(mat, uniforms2);
            const list = proceduralById.get(ba.id) ?? [];
            list.push(uniforms2);
            proceduralById.set(ba.id, list);
            objectBreathUniforms.push(uniforms2);
          }
        }
      });
    },
    detachSceneObject(id) {
      var _a2, _b, _c;
      const attached = attachedObjects.get(id);
      if (!attached) return;
      (_b = (_a2 = attached.loadedScene) == null ? void 0 : _a2.parent) == null ? void 0 : _b.remove(attached.loadedScene);
      (_c = attached.mixer) == null ? void 0 : _c.stopAllAction();
      attached.mixer = null;
      attachedObjects.delete(id);
    },
    /**
     * Apply a new transform to an already-attached scene object
     * without re-loading it. Used by the admin Objects tab so
     * number-input edits sync immediately to the preview mesh
     * (and the diff-and-reattach loop doesn't need to fire on
     * every transform change). No-op if id isn't attached.
     */
    setSceneObjectTransform(id, transform) {
      const attached = attachedObjects.get(id);
      if (!(attached == null ? void 0 : attached.loadedScene)) return;
      attached.loadedScene.position.set(...transform.position);
      attached.loadedScene.rotation.set(...transform.rotation);
      attached.loadedScene.scale.set(...transform.scale);
      attached.loadedScene.updateMatrixWorld(true);
      if ((activeTransformTool == null ? void 0 : activeTransformTool.targetId) === id) activeTransformTool.refresh();
    },
    setSceneObjectVisible(id, visible) {
      const attached = attachedObjects.get(id);
      if (!attached) {
        console.warn("[model3d-shader] setSceneObjectVisible: unknown id", id);
        return;
      }
      if (attached.spec.visibility === "always") {
        console.warn("[model3d-shader] setSceneObjectVisible: refusing to toggle always-visible object", id);
        return;
      }
      if (!attached.loadedScene) return;
      attached.loadedScene.visible = visible;
    },
    attachTransformGizmo(opts) {
      if (!scene || !camera || !renderer || !controls) return;
      const attached = attachedObjects.get(opts.objectId);
      if (!(attached == null ? void 0 : attached.loadedScene)) {
        console.warn("[model3d-shader] attachTransformGizmo: id not loaded yet", opts.objectId);
        return;
      }
      const targetScene = attached.loadedScene;
      const orbit = controls;
      transformGizmoCleanup == null ? void 0 : transformGizmoCleanup();
      const helper = new BoxHelper(targetScene, 16498468);
      helper.userData["__gizmoHelper"] = true;
      scene.add(helper);
      const transformControls = new TransformControls(camera, renderer.domElement);
      const transformHelper = transformControls.getHelper();
      transformHelper.userData["__gizmoHelper"] = true;
      scene.add(transformHelper);
      transformControls.setSpace("world");
      transformControls.setSize(0.85);
      let currentMode = opts.mode ?? "translate";
      const refreshTool = () => {
        targetScene.updateMatrixWorld(true);
        helper.update();
        transformHelper.updateMatrixWorld(true);
      };
      const emitChange = () => {
        refreshTool();
        opts.onChange({
          position: [targetScene.position.x, targetScene.position.y, targetScene.position.z],
          rotation: [targetScene.rotation.x, targetScene.rotation.y, targetScene.rotation.z],
          scale: [targetScene.scale.x, targetScene.scale.y, targetScene.scale.z]
        });
      };
      const applyMode = (mode) => {
        currentMode = mode;
        if (mode === "select") {
          transformControls.detach();
          orbit.enabled = true;
          return;
        }
        transformControls.attach(targetScene);
        transformControls.setMode(mode);
        orbit.enabled = true;
      };
      const onDraggingChanged = (event) => {
        orbit.enabled = event.value !== true;
      };
      const onObjectChange = () => {
        emitChange();
      };
      transformControls.addEventListener("dragging-changed", onDraggingChanged);
      transformControls.addEventListener("objectChange", onObjectChange);
      applyMode(currentMode);
      emitChange();
      transformGizmoCleanup = () => {
        var _a2;
        transformControls.removeEventListener("dragging-changed", onDraggingChanged);
        transformControls.removeEventListener("objectChange", onObjectChange);
        transformControls.detach();
        try {
          scene == null ? void 0 : scene.remove(transformHelper);
        } catch {
        }
        try {
          scene == null ? void 0 : scene.remove(helper);
        } catch {
        }
        try {
          transformControls.dispose();
        } catch {
        }
        try {
          (_a2 = helper.dispose) == null ? void 0 : _a2.call(helper);
        } catch {
        }
        orbit.enabled = true;
        transformGizmoCleanup = null;
        activeTransformTool = null;
      };
      activeTransformTool = { targetId: opts.objectId, setMode: applyMode, refresh: refreshTool };
    },
    setTransformGizmoMode(mode) {
      activeTransformTool == null ? void 0 : activeTransformTool.setMode(mode);
    },
    detachTransformGizmo() {
      transformGizmoCleanup == null ? void 0 : transformGizmoCleanup();
    },
    setOrbitControlsEnabled(enabled) {
      if (controls) {
        controls.enabled = enabled;
        console.log("[model3d-shader] setOrbitControlsEnabled", enabled);
      }
    },
    getColliders() {
      const out = [];
      if (model) {
        const charBox = new Box3().setFromObject(model);
        out.push({
          id: "__character__",
          kind: "character",
          worldMin: [charBox.min.x, charBox.min.y, charBox.min.z],
          worldMax: [charBox.max.x, charBox.max.y, charBox.max.z]
        });
      }
      for (const attached of attachedObjects.values()) {
        if (!attached.collider) continue;
        out.push({
          id: attached.spec.id,
          kind: attached.spec.kind,
          worldMin: attached.collider.min,
          worldMax: attached.collider.max
        });
      }
      return out;
    },
    detachAudio() {
      audioHandle == null ? void 0 : audioHandle.detach();
      audioHandle = null;
      if (lastAttachedAudioEl) {
        releaseAudioElement(lastAttachedAudioEl);
        lastAttachedAudioEl = null;
      }
      audioLevel = 0;
    },
    async speakText(text) {
      await speechAdapter.speakText(text);
    },
    onStateChange(listener) {
      stateListeners.add(listener);
      return () => stateListeners.delete(listener);
    },
    setExpression(name) {
      if (morphPack) {
        const before = currentExpression;
        applyExpressionLocal(name);
        if (before === currentExpression && name !== "Nature" && name !== "Neutral") {
          const known = morphPack.expressions.some((e) => e.id === name || e.label === name || e.aliases.includes(name));
          if (!known) throw new Error(`unknown expression: ${name}`);
        }
        return;
      }
      if (!pack) return;
      const isNature = name === "Nature";
      if (!isNature && !pack.expressions.some((e) => e.id === name)) {
        console.warn(`[model3d-shader] setExpression: unknown id "${name}". Available:`, ["Nature", ...pack.expressions.map((e) => e.id)]);
        throw new Error(`unknown expression: ${name}`);
      }
      if (expressionAutoReturnTimer !== null) {
        clearTimeout(expressionAutoReturnTimer);
        expressionAutoReturnTimer = null;
      }
      if (name === currentExpression && !expressionTransition) {
        scheduleExpressionAutoReturn(name);
        return;
      }
      uniforms.uEyeTex.value = getExpressionTexture(currentExpression);
      uniforms.uEyeTexNext.value = getExpressionTexture(name);
      uniforms.uExpressionOpacityFrom.value = getExpressionOpacity(currentExpression);
      uniforms.uExpressionOpacityTo.value = getExpressionOpacity(name);
      uniforms.uExpressionMix.value = 0;
      uniforms.uEyeBlend.value = 0;
      if (expressionFadeDuration <= 0) {
        currentExpression = name;
        const tex = getExpressionTexture(name);
        uniforms.uEyeTex.value = tex;
        uniforms.uEyeTexNext.value = tex;
        uniforms.uExpressionOpacityFrom.value = getExpressionOpacity(name);
        uniforms.uExpressionOpacityTo.value = getExpressionOpacity(name);
        uniforms.uExpressionMix.value = 0;
        expressionTransition = null;
        scheduleExpressionAutoReturn(name);
        return;
      }
      expressionTransition = { target: name, elapsed: 0, duration: expressionFadeDuration };
      scheduleExpressionAutoReturn(name);
    },
    setExpressionDurations(map) {
      const cleaned = {};
      for (const [k, v] of Object.entries(map)) {
        if (typeof v === "number" && Number.isFinite(v)) {
          cleaned[k] = Math.max(0, Math.min(10, v));
        }
      }
      expressionDurations = cleaned;
    },
    getExpressionNames() {
      if (morphPack) return morphPack.expressions.map((e) => e.id);
      return pack ? pack.expressions.map((e) => e.id) : [];
    },
    getMorphTargetNames() {
      return [...morphRegistry.keys()].sort((a, b) => a.localeCompare(b));
    },
    setMorphTarget(name, weight = 1) {
      if (!morphRegistry.has(name)) throw new Error(`unknown morph target: ${name}`);
      for (const target of morphRefs) {
        target.mesh.morphTargetInfluences[target.index] = 0;
      }
      for (const target of morphRegistry.get(name) ?? []) {
        target.mesh.morphTargetInfluences[target.index] = Math.max(0, Math.min(1, weight));
      }
    },
    setMorphMouthExpression(name) {
      if (name !== null && !getMorphExpression(name)) throw new Error(`unknown mouth expression: ${name}`);
      currentMouthExpressionOverride = name;
      applyMorphState();
    },
    setMorphBlinkExpression(name, weight = 1) {
      if (name !== null && !getMorphExpression(name)) throw new Error(`unknown blink expression: ${name}`);
      currentBlinkExpressionOverride = name;
      currentBlinkWeightOverride = name === null ? null : Math.max(0, Math.min(1, weight));
      applyMorphState();
    },
    getModelTextureInfo() {
      return modelTextureInfo.map((row) => ({ ...row }));
    },
    setMaterialOverrides(overrides) {
      materialOverrides = Array.isArray(overrides) ? overrides.map((entry) => ({ ...entry })) : [];
      applyMaterialOverridesToModel(model, materialOverrides);
    },
    getDefaultAnimationNames() {
      const oneShots = new Set(options.oneShotClips ?? []);
      return clips.map((c) => c.name).filter((n) => !oneShots.has(n));
    },
    getOneShotAnimationNames() {
      if (options.oneShotClips && options.oneShotClips.length > 0) {
        return options.oneShotClips.filter((n) => actions.has(n));
      }
      return clips.map((c) => c.name);
    },
    setDefaultAnimation(name) {
      if (!actions.has(name)) return;
      playDefaultAnimation(name);
    },
    playOneShotAnimation(name, count) {
      if (!actions.has(name)) return;
      playOneShotAnimationInternal(name, count);
    },
    updateProceduralParams(actionId, partial) {
      const list = proceduralById.get(actionId);
      if (!list || list.length === 0) return 0;
      let touched = 0;
      for (const uniforms2 of list) {
        if (partial.enabled !== void 0) uniforms2.uBreathEnabled.value = partial.enabled ? 1 : 0;
        if (partial.strength !== void 0) uniforms2.uBreathStrength.value = partial.strength;
        if (partial.speed !== void 0) uniforms2.uBreathSpeed.value = partial.speed;
        if (partial.maskStart !== void 0) uniforms2.uMaskStart.value = partial.maskStart;
        if (partial.maskSoftness !== void 0) uniforms2.uMaskSoftness.value = partial.maskSoftness;
        touched += 1;
      }
      return touched;
    },
    setModelRotationY(deg) {
      if (!model) return;
      const clamped = Math.max(-180, Math.min(180, deg));
      stageBaseYawDeg = clamped;
      model.rotation.y = clamped * Math.PI / 180;
    },
    setMouthFadeSpeed(value) {
      mouthFadeSpeed = clampParameterValue("mouthFadeSpeed", value);
    },
    setMouthOpacity(value) {
      mouthOpacity = clampParameterValue("mouthOpacity", value);
      uniforms.uMouthOpacity.value = mouthOpacity;
    },
    setBlinkConfig(cfg) {
      if (cfg.minInterval !== void 0) blinkMinInterval = clampParameterValue("blinkMinInterval", cfg.minInterval);
      if (cfg.maxInterval !== void 0) blinkMaxInterval = clampParameterValue("blinkMaxInterval", cfg.maxInterval);
      if (cfg.duration !== void 0) blinkDuration = clampParameterValue("blinkDuration", cfg.duration);
      if (blinkMinInterval > blinkMaxInterval) {
        const tmp = blinkMinInterval;
        blinkMinInterval = blinkMaxInterval;
        blinkMaxInterval = tmp;
      }
    },
    setExpressionFadeDuration(value) {
      expressionFadeDuration = clampParameterValue("expressionFadeDuration", value);
    },
    setVisemeDriftRate(hz) {
      visemeDriftRate = clampParameterValue("visemeDriftRate", hz);
    },
    setMouthVisemeFadeDuration(value) {
      mouthFadeScheduler.setDuration(clampParameterValue("mouthVisemeFadeDuration", value));
    },
    setMouthHoldDuration(value) {
      const clamped = clampParameterValue("mouthHoldDuration", value);
      mouthGate.setHoldSeconds(clamped);
    },
    setAudioLevel(rms01) {
      audioLevel = Math.max(0, Math.min(1, rms01));
    },
    setAutoReturnToNature(enabled) {
      autoReturnToNature = enabled;
    },
    dispatchActionPlan(plan) {
      if (!plan || !Array.isArray(plan.actions)) return;
      const actionsList = plan.actions;
      for (const a of actionsList) {
        if (a.type !== "parameter") continue;
        const value = clampParameterValue(a.id, a.value);
        switch (a.id) {
          case "mouthFadeSpeed":
            mouthFadeSpeed = value;
            break;
          case "mouthOpacity":
            mouthOpacity = value;
            uniforms.uMouthOpacity.value = mouthOpacity;
            break;
          case "blinkMinInterval":
            blinkMinInterval = value;
            break;
          case "blinkMaxInterval":
            blinkMaxInterval = value;
            break;
          case "blinkDuration":
            blinkDuration = value;
            break;
          case "expressionFadeDuration":
            expressionFadeDuration = value;
            break;
          case "visemeDriftRate":
            visemeDriftRate = value;
            break;
          case "mouthVisemeFadeDuration":
            mouthFadeScheduler.setDuration(value);
            break;
          case "mouthHoldDuration":
            mouthGate.setHoldSeconds(value);
            break;
        }
      }
      if (blinkMinInterval > blinkMaxInterval) {
        const tmp = blinkMinInterval;
        blinkMinInterval = blinkMaxInterval;
        blinkMaxInterval = tmp;
      }
      const lastExpr = [...actionsList].reverse().find((a) => a.type === "expression");
      if (lastExpr) {
        try {
          this.setExpression(lastExpr.id);
        } catch {
        }
      }
      const lastDefault = [...actionsList].reverse().find((a) => a.type === "defaultAnimation");
      if (lastDefault) {
        const clipName = lastDefault.clip ?? lastDefault.id;
        if (actions.has(clipName)) playDefaultAnimation(clipName);
      }
      for (const a of actionsList) {
        if (a.type === "oneShotAnimation") {
          const clipName = a.clip ?? a.id;
          const count = a.loopCount ?? 1;
          if (actions.has(clipName)) playOneShotAnimationInternal(clipName, count);
          else console.warn(`[model3d-shader] dispatchActionPlan oneShot: "${a.id}"${a.clip ? ` (clip: ${a.clip})` : ""} not found. Available:`, [...actions.keys()]);
        }
      }
      for (const a of actionsList) {
        if (a.type === "object") {
          const oa = a;
          this.setSceneObjectVisible(oa.id, oa.visible);
        }
      }
      if (autoReturnToNature && !lastExpr && currentExpression !== "Nature") {
        const currentDur = expressionDurations[currentExpression];
        const isSticky = typeof currentDur !== "number" || currentDur <= 0;
        if (!isSticky) {
          try {
            this.setExpression("Nature");
          } catch {
          }
        }
      }
    },
    dispose() {
      var _a2, _b, _c;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      controls == null ? void 0 : controls.dispose();
      controls = null;
      mixer == null ? void 0 : mixer.stopAllAction();
      mixer = null;
      actions.clear();
      defaultAction = null;
      oneShotAction = null;
      for (const timer of animationStopTimers) clearTimeout(timer);
      animationStopTimers = [];
      transformGizmoCleanup == null ? void 0 : transformGizmoCleanup();
      for (const attached of attachedObjects.values()) {
        (_b = (_a2 = attached.loadedScene) == null ? void 0 : _a2.parent) == null ? void 0 : _b.remove(attached.loadedScene);
        (_c = attached.mixer) == null ? void 0 : _c.stopAllAction();
      }
      attachedObjects.clear();
      if (container) {
        const ro = container.__m3dResizeObserver;
        ro == null ? void 0 : ro.disconnect();
        const gazeCleanup = container.__m3dGazeCleanup;
        gazeCleanup == null ? void 0 : gazeCleanup();
      }
      headGazeBone = null;
      keyLight = null;
      if (scene && model) scene.remove(model);
      model = null;
      texturePack == null ? void 0 : texturePack.dispose();
      texturePack = null;
      morphPack = null;
      morphRegistry.clear();
      morphRefs.length = 0;
      controlledMorphBinds.length = 0;
      modelTextureInfo = [];
      pack = null;
      characterMaterial.dispose();
      staticCharacterMaterial.dispose();
      renderer == null ? void 0 : renderer.dispose();
      renderer = null;
      scene = null;
      camera = null;
      audioHandle == null ? void 0 : audioHandle.detach();
      audioHandle = null;
      if (lastAttachedAudioEl) {
        releaseAudioElement(lastAttachedAudioEl);
        lastAttachedAudioEl = null;
      }
      speechAdapter.stop();
      canvas == null ? void 0 : canvas.remove();
      canvas = null;
      container = null;
      stateListeners.clear();
    }
  };
}
const TEXTURE_SUBDIR = "Texture";
async function probeImage(url) {
  try {
    const res = await fetch(url, { method: "HEAD" });
    if (!res.ok) return false;
    const ct = res.headers.get("content-type");
    if (ct === null) return true;
    return ct.toLowerCase().startsWith("image/");
  } catch {
    return false;
  }
}
async function probeAny(url) {
  try {
    const res = await fetch(url, { method: "HEAD" });
    return res.ok;
  } catch {
    return false;
  }
}
function joinUrl(folder, ...parts) {
  const trimmed = folder.replace(/\/+$/, "");
  return [trimmed, ...parts].join("/");
}
function lastPathSegment(folderUrl) {
  const trimmed = folderUrl.replace(/\/+$/, "");
  const idx = trimmed.lastIndexOf("/");
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}
function deriveLabel(id) {
  if (id.length === 0) return id;
  return id.charAt(0).toUpperCase() + id.slice(1);
}
async function resolveModelUrl(folderUrl) {
  const manifestUrl = joinUrl(folderUrl, "manifest.json");
  try {
    const res = await fetch(manifestUrl, { method: "GET" });
    if (res.ok) {
      const ct = res.headers.get("content-type") ?? "";
      if (ct.toLowerCase().includes("json")) {
        const json = await res.json();
        if (typeof json.glb === "string" && json.glb.length > 0) {
          return joinUrl(folderUrl, json.glb);
        }
      }
    }
  } catch {
  }
  const basename = lastPathSegment(folderUrl);
  const conventional = joinUrl(folderUrl, `${basename}.glb`);
  if (await probeAny(conventional)) {
    return conventional;
  }
  throw new Error(
    `[loadFolderPack] no GLB found at ${folderUrl}: tried manifest.json (.glb field) and ${conventional}`
  );
}
async function loadFolderPack(folderUrl) {
  const textureRoot = joinUrl(folderUrl, TEXTURE_SUBDIR);
  const mandatorySlots = Object.entries(MANDATORY_PACK_FILENAMES);
  const mandatoryProbes = await Promise.all(
    mandatorySlots.map(async ([slot, filename]) => ({
      slot,
      filename,
      url: joinUrl(textureRoot, filename),
      present: await probeImage(joinUrl(textureRoot, filename))
    }))
  );
  const missing = mandatoryProbes.filter((p) => !p.present);
  if (missing.length > 0) {
    const names = missing.map((p) => p.filename).join(", ");
    throw new Error(
      `[loadFolderPack] pack at ${folderUrl} missing mandatory textures: ${names}`
    );
  }
  const slotUrl = (slot) => mandatoryProbes.find((p) => p.slot === slot).url;
  const optionalProbes = await Promise.all([
    ...KNOWN_OPTIONAL_EYE_EXPRESSION_IDS.map(async (id) => ({
      id,
      kind: "eye",
      url: joinUrl(textureRoot, `Eye_${id}.png`),
      present: await probeImage(joinUrl(textureRoot, `Eye_${id}.png`))
    })),
    ...KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS.map(async (id) => ({
      id,
      kind: "mouth",
      url: joinUrl(textureRoot, `Mouse_${id}.png`),
      present: await probeImage(joinUrl(textureRoot, `Mouse_${id}.png`))
    }))
  ]);
  const expressions = optionalProbes.filter((p) => p.present).map((p) => ({ id: p.id, label: deriveLabel(p.id), url: p.url }));
  const modelUrl = await resolveModelUrl(folderUrl);
  const pack = {
    base: slotUrl("base"),
    eyeMask: slotUrl("eyeMask"),
    eyeNormal: slotUrl("eyeNormal"),
    eyeBlink: slotUrl("eyeBlink"),
    mouthMask: slotUrl("mouthMask"),
    visemes: {
      // Idle viseme reuses the base texture — no separate file in the v2 convention.
      idle: slotUrl("base"),
      A: slotUrl("visemeA"),
      E: slotUrl("visemeE"),
      I: slotUrl("visemeI"),
      O: slotUrl("visemeO"),
      U: slotUrl("visemeU")
    },
    expressions
  };
  return { pack, modelUrl };
}
function resolveDefaultClipName(model3d) {
  if (!model3d) return null;
  const labels = model3d.clipLabels ?? {};
  const defaults = model3d.defaultClipLabels ?? [];
  if (defaults.length === 0) return null;
  const target = defaults[0];
  for (const [clipName, label] of Object.entries(labels)) {
    if (label === target) return clipName;
  }
  return null;
}
function applyPersistedModel3DConfig(runtime, character) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
  if (!runtime) return;
  const m = character.model3d ?? null;
  if (!m) return;
  const rp = m.renderParams;
  if (rp) {
    if (rp.mouthFadeSpeed !== void 0) (_a = runtime.setMouthFadeSpeed) == null ? void 0 : _a.call(runtime, rp.mouthFadeSpeed);
    if (rp.mouthOpacity !== void 0) (_b = runtime.setMouthOpacity) == null ? void 0 : _b.call(runtime, rp.mouthOpacity);
    if (rp.blinkMinInterval !== void 0 || rp.blinkMaxInterval !== void 0 || rp.blinkDuration !== void 0) {
      (_c = runtime.setBlinkConfig) == null ? void 0 : _c.call(runtime, {
        minInterval: rp.blinkMinInterval,
        maxInterval: rp.blinkMaxInterval,
        duration: rp.blinkDuration
      });
    }
    if (rp.expressionFadeDuration !== void 0) (_d = runtime.setExpressionFadeDuration) == null ? void 0 : _d.call(runtime, rp.expressionFadeDuration);
    if (rp.visemeDriftRate !== void 0) (_e = runtime.setVisemeDriftRate) == null ? void 0 : _e.call(runtime, rp.visemeDriftRate);
    if (rp.mouthVisemeFadeDuration !== void 0) (_f = runtime.setMouthVisemeFadeDuration) == null ? void 0 : _f.call(runtime, rp.mouthVisemeFadeDuration);
    if (rp.mouthHoldDuration !== void 0) (_g = runtime.setMouthHoldDuration) == null ? void 0 : _g.call(runtime, rp.mouthHoldDuration);
  }
  const defaultClip = resolveDefaultClipName(m);
  if (defaultClip) (_h = runtime.setDefaultAnimation) == null ? void 0 : _h.call(runtime, defaultClip);
  if (((_i = m.viewportPose) == null ? void 0 : _i.rotationY) !== void 0) {
    (_j = runtime.setModelRotationY) == null ? void 0 : _j.call(runtime, m.viewportPose.rotationY);
  }
  if (m.autoReturnToNature !== void 0) {
    (_k = runtime.setAutoReturnToNature) == null ? void 0 : _k.call(runtime, m.autoReturnToNature);
  }
  if (m.expressionDurations) {
    (_l = runtime.setExpressionDurations) == null ? void 0 : _l.call(runtime, m.expressionDurations);
  }
  if (m.materialOverrides) {
    (_m = runtime.setMaterialOverrides) == null ? void 0 : _m.call(runtime, m.materialOverrides);
  }
}
export {
  KNOWN_OPTIONAL_EYE_EXPRESSION_IDS2 as KNOWN_OPTIONAL_EYE_EXPRESSION_IDS,
  KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS2 as KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS,
  MALFEUERA_SAMPLE_MODEL_URL,
  MALFEUERA_SAMPLE_PACK,
  MANDATORY_PACK_FILENAMES2 as MANDATORY_PACK_FILENAMES,
  Model3DActionPlanSchema,
  Model3DActionSchema,
  Model3DTexturePackSchema,
  OL_SAMPLE_MODEL_URL,
  OL_SAMPLE_PACK,
  PARAMETER_IDS,
  applyPersistedModel3DConfig,
  attachAudioElement,
  clampParameterValue2 as clampParameterValue,
  createShaderComposed3DController,
  createSpeechAdapter,
  loadFolderPack,
  pickViseme,
  resolveDefaultClipName
};
//# sourceMappingURL=model3d-runtime.js.map
